import {
  EmitterKeys,
  LoyaltyChallengeConfig,
  ScriptPageKeys,
} from '@shared/models';

import {
  BARCODE_FORMAT,
  CampaignTypes,
  CetoineSubType,
  DeviceType,
  DISPLAY_CONDITION,
  Pages,
  ProductCheckTabType,
  RewardFormat,
  ScanCheckMode,
  SubType,
  TitleType,
  Variants,
} from '../utils';

export const CETOINE_CONFIG: LoyaltyChallengeConfig = {
  currency: 'EUR',
  appIcon: 'assets/images/cetoine/favicon.ico',
  dialogVerticalOffset: -100,
  cssVariables: {
    'font-family-bold': 'ProximaNova-Bold',
    'font-family': 'ProximaNova',
    'font-family-medium': 'ProximaNova-Medium',
    'font-family-light': 'ProximaNova-Light',
    'font-family-proximanova-extrabold': 'ProximaNova-ExtraBold',
    'navbar-height': '70px',
    'navbar-background-color': '#ffffff',

    'nav-link-color': '#121212',
    'nav-link-hover-color': '#004CAB',
    'nav-link-active-color': '#004CAB',

    'nav-item-bg-color': 'white',
    'nav-item-font-family': 'ProximaNova',
    'nav-item-active-bg-color': 'white',
    'nav-item-hover-bg-color': 'rgb(0 0 0 / 10%)',

    'body-color': '#000000',
    'body-background-color': '#ffffff',

    'form-field-input-color': '#000000',

    'challenge-primary-color': '#00529f',

    'tooltip-text-color': 'white',
    'tooltip-background-color': 'black',

    // Onboarding (EAI-1807) — Carrefour "Challenge Fid'" charter: orange surface
    // with purple ink and purple CTAs. These override the green/gold/white
    // defaults baked into the onboarding component styles.
    //
    // Immersive surface: flat Carrefour orange (no background artwork).
    'hdr-bg': '#F59E25',
    // Headline/CTA/label text uses the Carrefour display face (body copy —
    // legal note, leads — stays on the body font).
    'ob-font-display': 'ProximaNova-ExtraBold, ProximaNova, sans-serif',
    // On-surface ink. `#5E2A78` is the brand purple (#6F2F88) nudged darker so
    // it clears WCAG AA (4.7:1) on the orange even for small text — the exact
    // brand purple only reaches 4.0:1 (fails AA for body copy).
    'ob-ink': '#5E2A78',
    // "CHALLENGE FID' / AVEC LE" lockup is set in caps.
    'ob-title-transform': 'uppercase',
    // "Jusqu'à" kicker of the promise lockup: orange on a purple chip.
    'ob-promise-kicker-bg': '#5E2A78',
    'ob-promise-kicker-ink': '#F59E25',
    // Caption pill under the challenge preview: purple ink on orange.
    'ob-pill-bg': '#F59E25',
    'ob-pill-ink': '#5E2A78',
    // Selection progress bar: purple fill on the design's darker amber track.
    'ob-progress-fill': '#5E2A78',
    'ob-progress-track': '#CB7D11',
    // Selection toast: off-white card with a purple tick.
    'ob-toast-bg': '#FFFDF2',
    'ob-toast-ink': '#2C3136',
    'ob-toast-check-bg': 'rgba(94, 42, 120, 0.18)',
    'ob-toast-check-ink': '#5E2A78',
    // Swipe "pass" button: solid white with purple ink (vs the default ghost).
    'ob-pass-bg': '#FFFFFF',
    'ob-pass-ink': '#5E2A78',
    'ob-pass-border': '#5E2A78',
    // Purple action colour: primary CTAs + "I want it" swipe button (white ink).
    // Reuses the AA-safe purple.
    'swipe-gold': '#5E2A78',
    'swipe-gold-ink': '#FFFFFF',
    // Cards stacked behind the top swipe card: deep purple (charter).
    'swipe-stack-bg': '#4A2168',
    // Promise artwork: the full headline lockup, so it spans the column rather
    // than sitting as a small mark at the top right.
    'ob-promise-align': 'center',
    'ob-promise-w': '100%',
    // Also capped against the viewport height: on a short phone the welcome
    // screen is one column of fixed-ratio artwork, and the legal note under
    // the CTA is what falls off the bottom first.
    'ob-promise-maxw': 'min(420px, 34dvh)',
    'ob-promise-ratio': '627 / 410',
    'ob-stack-maxw': 'min(380px, 38dvh)',
    // Rectangular CTAs with a small radius (vs the default pill).
    'ob-cta-radius': '10px',
  },
  rewardFormat: RewardFormat.PRICE,
  og: {
    title: 'Challenges Fidélité | Carrefour',
    description:
      "Découvrez les Challenges Fidélité Carrefour et gagnez jusqu'à 50€ de remise sur vos prochains achats.",
  },
  rewardTemplate:
    '{points}<div style="font-family: var(--font-family-bold); color: white; font-size: 15px; margin-top: 0.125rem;">points</div>',
  pages: {
    availablePages: [
      'help',
      'login',
      'history',
      'current',
      'current-bis',
      'cookie-policy',
    ],
    help: {
      meta: {
        title: 'Challenges Fidélité | Carrefour | Aide',
        keywords:
          'Carrefour, Fidélité, Challenges, Avantages, Promotions, Catalogue',
        description:
          "Découvrez les Challenges Fidélité Carrefour et gagnez jusqu'à 50€ de remise sur vos prochains achats.",
      },
      header: {
        background: '#F59E25',
        bannerAlternativeText:
          'Bannière avec liens vers Aide et Mentions légales',
        bannerUrl: {
          mobile: 'assets/images/cetoine/mobile-cover.webp',
          tablet: 'assets/images/cetoine/tablet-cover.webp',
          desktop: 'assets/images/cetoine/desktop-cover.webp',
        },
        goBackButton: {
          text: 'Retour à mes défis',
          backgroundColor: '#0970E6',
          color: 'white',
          fontFamily: 'var(--font-family-bold)',
          borderRadius: '5px',
        },
        title: {
          text: 'Aide & Mentions légales',
          styles: {
            margin: 'unset',
            padding: '4rem',
            fontSize: '35px',
            color: '#6F2F88',
            textAlign: 'center',
            textTransform: 'uppercase',
            fontFamily: 'ProximaNova-ExtraBold',
          },
        },
        howToPlay: {
          specificDisplayCondition: DISPLAY_CONDITION.NEVER,
          title: {
            color: 'white',
            text: 'Comment ça marche ?',
            fontFamily: 'var(--font-family-bold)',
          },
        },
        backButtonTemplate: [
          '<div class="go-back" style="display: flex; justify-content: center; align-items: center; border-radius: 22px; border: solid 1.5px #00529f; width: 160px; height: 40px; background-color: white">',
          '<p style="color: #00529f; margin: 0; font-family: var(--font-family-medium); font-size: 12px">Retour à la <span style="font-family: var(--font-family-bold);">connexion</span> </p>',
          '</div>',
        ],
      },
      faqVariant: {
        variant: Variants.VARIANT_1,
        title: {
          displayWhenCampaignBis: false,
          type: TitleType.TEXT,
          label:
            '<h1 style="color: #004CAB; margin: unset;font-family: var(--font-family-bold); font-size: 30px; line-height: normal;">Challenge Fid’</h1>',
          styles: {},
        },
        questions: [
          {
            title: 'Suis-je obligé de sélectionner 10 Challenges ?  ',
            content:
              'Relevez autant de challenges que vous voulez dans une limite de 10 par mois !',
          },
          {
            title: 'Est-ce que je peux changer de challenge ?',
            content:
              "Vous pouvez changer d’avis seulement jusqu'à votre premier passage en caisse ou votre première commande Drive. Une fois que vous avez commencé à cumuler des achats sur l'un de vos challenges, vous ne pouvez plus l’arrêter.  ",
          },
          {
            title:
              "Si je dépasse mon objectif d'achat, est-ce que je continue d’augmenter ma cagnotte ?",
            content:
              'Dès lors que vous avez atteint le troisième palier, vous sécurisez le gain associé sur votre cagnotte ! Quand vous avez remporté tout le défi, chaque achat supplémentaire ne fera pas croître votre cagnotte car le gain est limité à l’atteinte du troisième palier.',
          },
          {
            title:
              'Puis-je cumuler mes achats entre différentes enseignes ou drives Carrefour ?',
            content:
              'Oui, tous vos achats comptent tant que vous enregistrez votre Carte Club ou Carte PASS à chaque achat. Vous cumulez des réductions à chacun de vos achats dans tous les points de vente physiques Carrefour (Hypermarchés, Market, Contact, City, Express), au Drive et en livraison à domicile (Carrefour Livré Chez Vous).',
          },
          {
            title:
              'Est-ce que l’achat de boissons alcoolisées fait progresser mes challenges ?',
            content:
              'Non, les boissons alcoolisées ne sont pas incluses dans les produits éligibles aux challenges, et de ce fait, les achats de ces produits ne font pas progresser vos challenges.',
          },
          {
            title: 'Comment se calcule ma cagnotte Challenge Fid’ ?',
            content:
              'En achetant vos marques préférées dans vos magasins Carrefour et au Drive, vous faites progresser vos challenges. A chaque palier atteint, vous cumulez des euros sur votre cagnotte.',
          },
          {
            title: "Comment suivre l'avancement de mes challenges ?",
            content:
              'Votre page personnelle Challenge Fid’ est mise à jour le lendemain de vos achats ou le jour de votre retrait Drive dans les enseignes Carrefour. Trois paliers par challenge vous indiqueront la progression des euros cumulés sur votre cagnotte jusqu’à la fin du mois. Vous accédez à votre page personnalisée depuis la page <a href="https://challengefid.carrefour.fr/" target="_blank" title="Accéder à la page de connexion des Challenges Fid’">challengefid.carrefour.fr.</a> Pensez à enregistrer votre page personnalisée dans vos favoris pour y accéder à tout moment !',
          },
          {
            title:
              'Les achats en réduction peuvent-ils être compris dans des challenges ?',
            content:
              "Oui, des achats en promotions peuvent être comptabilisés pour Challenge FId’. En cas de remise, c'est le montant après remise qui sera pris en compte dans le compteur du programme. Dans chaque challenge le bouton «Produits éligibles » vous permet de vérifier l’éligibilité de vos articles.",
          },
          {
            title: 'Quand est-ce que ma cagnotte sera versée ?',
            content:
              "Le montant que vous avez cagnotté durant l'opération est versé sur votre Carte Club ou Carte PASS au plus tard six jours après la fin de l’opération.",
          },
        ],
        accordion: {
          styles: {
            title: {
              fontSize: '14px',
              padding: '10px 0',
              minWidth: '15rem',
            },
            description: {
              color: '#6b767c',
              fontSize: '12px',
              paddingTop: '0.5rem',
            },
          },
          alternativeTextExpandMore: 'Réduire la section',
          alternativeTextExpandLess: 'Développer la section',
        },
      },
      templateAfterForm: [
        '<div style="padding-top: 5rem;margin: 0 auto; width: 80%;">',
        '<h1 style="font-size: 24px;color: #095ba5; font-family: ProximaNova-Bold; text-align:center">Modalités Challenge Fid’ Carrefour </h1>',
        '<h2 style="font-size: 16px;color: #095ba5; font-family: ProximaNova; margin: 20px 0; text-align:center">TOUS LES MOIS, DU 1ER AU DERNIER JOUR DU MOIS </h2>',
        '<h3 style="font-size: 14px;font-family: ProximaNova-Bold;">1- Présentation </h3>',
        '<p style="font-size: 12px;font-family: ProximaNova;"> Le Programme de Fidélité « Mon Club Carrefour » est un Programme de Fidélisation de clientèle, proposé en France métropolitaine (Corse et Monaco compris), par la Société Carrefour France, SAS au capital de 1 994 899 667,21 euros, dont le siège social se situe ZI Route de Paris, 14100 Mondeville, immatriculée au Registre du Commerce et des Sociétés de Caen sous le numéro 672 050 085 (ci-après désignée « la Société Carrefour France »).</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">La Carte Club Carrefour permet à son bénéficiaire, sur simple présentation de celle-ci lors de son passage en caisse, de profiter de réductions de prix sur certains produits (les « Avantages Club »).</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Il est précisé que les titulaires d’une Carte PASS, qui ont adhéré au Programme Club Carrefour, n’ont pas besoin de présenter de Carte Club en caisse. Le paiement de leurs achats avec leur Carte PASS leur permet de bénéficier des Avantages Club.</p>',
        '<p style="margin-bottom: 0;font-size: 12px;font-family: ProximaNova;">Les conditions du Programme Club Carrefour sont disponibles sur le site</p>',
        '<a style="font-size: 12px;font-family: ProximaNova;" target="_blank" href="https://www.carrefour.fr/services/carte-carrefour/conditions-generales" title="Accéder aux conditions générales de Carrefour">http://www.carrefour.fr/services/carte-carrefour/conditions-generales </a>',
        '<p style="margin-top: 1rem;font-size: 12px;font-family: ProximaNova;">La Société Carrefour propose à des adhérents du Programme Club Carrefour (ci-après dénommés les « Adhérents ») de bénéficier de nouveaux avantages dénommés « Challenge Fid’ Carrefour» tels que décrits à l’article 3, valables dans les magasins suivants, situés en France métropolitaine, Corse comprise <span style="font-style: italic;">(ci-après dénommés les « Magasins Participants »):</span></p>',
        '<ul style="margin-left: 25px;">',
        '<li style="font-size: 12px;font-family: ProximaNova;">Carrefour,</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">Carrefour Market,</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">Market,</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">Carrefour Drive,</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">Carrefour Livré Chez Vous,</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">Carrefour City,</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">Carrefour Contact,</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">Carrefour Express et</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">Carrefour Montagne.</li>',
        '</ul>',
        '<p style="font-size: 12px;font-family: ProximaNova;">L’opération commerciale « Challenge Fid’ Carrefour » est valable du 1er au dernier jour de chaque mois <span style="font-style: italic;">(ci-après dénommée « la Période »)</span>.</p>',

        '<h3 style="font-size: 14px;font-family: ProximaNova-Bold;">2- Inscription </h3>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="font-family: ProximaNova-Bold;">2.1.</span> Les Adhérents seront contactés par courrier électronique le 1er jour du challenge, via leur adresse électronique principale renseignée dans le cadre du Programme Club Carrefour, pour les informer du lancement de cette opération commerciale. À défaut d\'être contacté par courrier électronique, l\'Adhérent peut également visualiser chaque mois les nouveaux challenges depuis l\'application Carrefour et sur le site carrefour.fr dès le 1er de chaque mois. Sur l’application Carrefour ou sur le site internet <a target="_blank" href="https://www.carrefour.fr/fidelite/challenge-fid" title="Accéder à la page Challenge Fid’ Carrefour" >www.carrefour.fr/fidelite/challenge-fid</a>, ’Adhérent devra sélectionner le ou les challenges qu’ils souhaitent réaliser, durant la période d’inscription du 1er au dernier jour du mois, dans la limite de 10 challenges parmi les 20 qui sont proposés. Passé ce délai, aucune inscription ne sera possible.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Seules les personnes physiques qui ont adhéré au Club Carrefour peuvent s’inscrire au « Challenge Fid’ Carrefour» et bénéficier des Avantages « Challenge Fid’ Carrefour ». Une seule inscription est autorisée par foyer (personnes vivant sous le même toit).</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Les Adhérents autorisent toutes les vérifications concernant leur identité et leur domicile par la Société Carrefour, sans toutefois qu’elle ait l’obligation de procéder à une vérification systématique de l’ensemble des formulaires d’inscription reçus.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">L’inscription étant limitée à 1 (une) par foyer, toute tentative de fraude de la part d’un Adhérent pourra entraîner la nullité de son inscription.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Aucune inscription à ce programme par courrier ou par téléphone ne sera prise en compte.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="font-family: ProximaNova-Bold;">2.2.</span>Une fois que l’Adhérent aura pris connaissance et accepté les présentes modalités, il pourra découvrir les marques qui lui sont proposées et choisir le ou les challenges qu’il souhaite réaliser.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">L’Adhérent aura la possibilité de choisir au maximum 10 challenges parmi les 20 qui lui seront proposés. Chaque Adhérent devra réaliser son ou ses challenges au plus tard le dernier jour du mois. À noter que les commandes Drive devront être retirées avant le dernier jour du mois pour que les challenges soient pris en compte.</p>',
        '<h3 style="font-size: 14px;font-family: ProximaNova-Bold;">3- Les Avantages « Challenge Fid’ Carrefour » </h3>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="font-family: ProximaNova-Bold;">3.1.</span> Afin de gagner son ou ses challenges et bénéficier de la remise correspondante, l’Adhérent doit acheter des produits déterminés dans les Magasins Participants et atteindre un montant d’achat minimum pour ces produits pendant la Période pour atteindre les différents paliers permettant de cumuler des euros sur la cagnotte Club Carrefour. Le challenge à atteindre est déterminé par la Société Carrefour sur la base de l’historique d’achat de l’Adhérent sur une période donnée.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">La liste des produits éligibles pour chaque challenge et le montant minimum d’achat à atteindre est disponible dès le 1er du mois, sur l’application Carrefour et sur le Site.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Chaque achat de produit éligible au challenge sera comptabilisé par la Société Carrefour. Le seuil indicatif d’avancement de chaque challenge est consultable par l’Adhérent sur l’application Carrefour ou sur le site.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Le montant des achats pris en compte dans l’avancement du challenge sera le montant net après remises, à la date de mise à jour figurant en haut à droite du site.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Par exemple, si un produit X est vendu 2 € avec 0.20 € de remise immédiate en caisse, la société Carrefour prendra en compte le montant réellement payé par l’Adhérent pour le calcul du Challenge, à savoir 1.80€.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="font-family: ProximaNova-Bold;">3.2.</span> Pour chaque challenge proposé à l’Adhérent, la Société Carrefour aura préalablement défini des paliers de remises qui seront accordés pour un montant d’achat correspondant au seuil du challenge. Les paliers de remises et les seuils d’achats correspondants de chaque challenge proposé à l’Adhérent seront précisés sur l\'application Carrefour ou sur le site.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Pour chaque challenge remporté, soit une fois le seuil d’achat atteint, le palier de remise correspondant s’applique pour les achats réalisés dans la Période.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">A la fin de la Période, l’Adhérent bénéficiera d’un Avantage Club (remise sur les produits achetés pendant la Période) qui sera crédité sur sa Cagnotte Club Carrefour.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Une limite maximum de remise est néanmoins fixée à cinquante (50) euros par Adhérent, sur la totalité des 10 challenges réalisés tout au long de la Période.</p>',
        '<ul style="margin-left: 25px;">',
        '<li style="font-size: 12px;font-family: ProximaNova;margin-bottom: 1rem;"><span style="font-style: italic;">Exemple 1 :</span> L’Adhérent Z choisit un challenge sur la marque X. Pour ce challenge, l’Adhérent Z devait acheter pour 5 € de produits à marque X pour atteindre le premier palier de remise. Si le challenge n’est pas atteint à la fin de la Période, l’Adhérent Z ne bénéficie d’aucun Avantage Club sur les achats réalisés.</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;margin-bottom: 1rem;"><span style="font-style: italic;">Exemple 2 :</span> L’Adhérent Z choisit un challenge sur la marque X avec un premier palier à 0,75€ de remise pour 5€ d’achats sur cette marque X, un 2ème palier à 1,40€ de remise pour 7€ d’achats sur la marque X et un 3ème palier à 2,50€ de remise pour 10€ d’achats sur la marque X. A la fin de la Période, la Société Carrefour a comptabilisé des achats sur la marque X pour un montant de 12€. L’adhérent Z verra sa Cagnotte Club Carrefour créditée de 2,50€ dans le mois suivant la fin du challenge, ce montant correspondant à l’atteinte du 3ème palier d’achat sur la marque X.</li>',
        '</ul>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Ce montant maximum est garanti par la Société Carrefour sous réserve de la réalisation de la totalité (100% de progression) des 10 challenges sélectionnés par le client au cours d\'un mois calendaire. Dans l\'hypothèse où le cumul des gains des 10 challenges finalisés serait inférieur à 50€, la Société Carrefour procède à un abondement automatique sur la Cagnotte Club Carrefour pour atteindre un gain total de 50€. Tout challenge non finalisé ou non activé ne pourra donner droit à cet abondement.</p>',

        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="text-decoration: underline;">Exemple :</span> L\'Adhérent bénéficie, grâce à la complétion de ses 10 challenges, d’un montant réel cumulé de 38,80€. Dans ce cas, la Société Carrefour verse sur la Cagnotte Club Carrefour la somme totale de 50€, correspondant au montant réel acquis complété d\'un abondement de 11,20€. Si toutefois seuls neuf (9) challenges sur dix (10) sont finalisés au terme du mois calendaire, seul le montant réel des avantages acquis (hors abondement) est crédité sur la Cagnotte Club Carrefour, selon les paliers de progression effectivement atteints.</p>',
        '<h3 style="font-size: 14px;font-family: ProximaNova-Bold;">4- Les challenges bonus "Boost Fid"" </h3>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="font-family: ProximaNova-Bold;">4.1.</span> Les Adhérents seront contactés par courrier électronique le 1er jour du challenge, via leur adresse électronique principale renseignée dans le cadre du Programme Club Carrefour, pour les informer du lancement de cette opération commerciale. À défaut d\'être contacté par courrier électronique, l\'Adhérent peut également visualiser les nouveaux challenges depuis l\'application Carrefour et sur le site <a target="_blank" title="Accéder au site Carrefour" href="https://www.carrefour.fr/">carrefour.fr.</a> Pour s’inscrire, ils doivent se rendre dans la section “Boost Fid’” sur la page “Challenge Fid’” du site internet <a target="_blank" href="https://www.carrefour.fr/fidelite/challenge-fid" title="Accéder à la page Challenge Fid’ Carrefour"> https://www.carrefour.fr/fidelite/challenge-fid </a> u sur l’application Carrefour pour sélectionner le ou les challenges qu’ils souhaitent réaliser, durant la période d’inscription représentée par les dates de validité du “Boost Fid’ Carrefour”.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Seules les personnes physiques qui ont adhéré au Programme Club Carrefour peuvent s’inscrire au « Boost Fid’ Carrefour» et bénéficier des Avantages « Boost Fid’ Carrefour». Une seule inscription est autorisée par foyer (personnes vivant sous le même toit).</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Les Adhérents autorisent toutes les vérifications concernant leur identité et leur domicile par la Société Carrefour, sans toutefois qu’elle ait l’obligation de procéder à une vérification systématique de l’ensemble des  formulaires d’inscription reçus.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">L’inscription étant limitée à 1 (une) par foyer, toute tentative de fraude de la part d’un Adhérent pourra entraîner la nullité de son inscription.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Aucune inscription à ce programme par courrier ou par téléphone ne sera prise en compte.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="font-family: ProximaNova-Bold;">4.2.</span> Une fois que l’Adhérent aura pris connaissance et accepté les présentes modalités, il pourra découvrir les marques qui lui sont proposées et choisir le ou les challenges qu’il souhaite réaliser.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Chaque Adhérent devra réaliser son ou ses challenges au plus tard le dernier jour de validité du challenge bonus concerné.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="font-family: ProximaNova-Bold;">4.3.</span> Afin de gagner son ou ses challenges et bénéficier des avantages correspondants, l’Adhérent doit acheter des produits déterminés dans les magasins participants et atteindre un montant d’achat minimum pour ces produits pendant la Période, pour atteindre les paliers permettant de cagnotter des euros sur la cagnotte Club Carrefour. Le challenge à atteindre est déterminé par la Société Carrefour sur la base de l’historique d’achat de l’Adhérent sur une période donnée.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">La liste des produits éligibles pour chaque challenge et le montant minimum d’achat à atteindre sont disponibles dans la section Boost Fid’ du site <a target="_blank" href="https://www.carrefour.fr/fidelite/challenge-fid" title="Accéder à la page Challenge Fid’ Carrefour">https://www.carrefour.fr/fidelite/challenge-fid</a> ou sur l’application Carrefour.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Chaque achat de produit éligible au challenge sera comptabilisé par la Société Carrefour. Le seuil indicatif d’avancement de chaque challenge est consultable par l’Adhérent dans la section Boost Fid’ du site <a target="_blank" href="https://www.carrefour.fr/fidelite/challenge-fid" title="Accéder à la page Challenge Fid’ Carrefour">https://www.carrefour.fr/fidelite/challenge-fid</a> ou sur l’application Carrefour.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Le montant des achats pris en compte dans l’avancement du challenge sera le montant net après remises, à la date de mise à jour figurant en haut à droite du site.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Par exemple, si un produit X est vendu 2 € avec 0.20 € de remise immédiate en caisse, la Société Carrefour prendra en compte le montant réellement payé par l’Adhérent pour le calcul du challenge, à savoir 1.80€.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="font-family: ProximaNova-Bold;">4.4.</span> Pour chaque challenge proposé à l’Adhérent, la Société Carrefour aura préalablement défini des paliers de remises qui seront accordés pour un montant d’achat correspondant au seuil du challenge. Les paliers de remises et les seuils d’achats correspondants de chaque challenge proposé à l’Adhérent seront précisés dans la section Boost Fid’ du site <a target="_blank" href="https://www.carrefour.fr/fidelite/challenge-fid" title="Accéder à la page Challenge Fid’ Carrefour">https://www.carrefour.fr/fidelite/challenge-fid</a> ou sur l’application Carrefour. Pour chaque challenge remporté, soit une fois le seuil d’achat atteint, le palier de remise correspondant s’applique pour les achats réalisés dans la Période.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">A la fin de la Période, l’Adhérent bénéficiera d’un Avantage Fidélité (remise sur les produits achetés pendant la Période) qui sera crédité sur son Compte Club Carrefour.</p>',
        '<h3 style="font-size: 14px;font-family: ProximaNova-Bold;">5- Disponibilité et limitation du nombre d\'activations des Challenges </h3>',
        "<p style=\"font-size: 12px;font-family: ProximaNova;\">Le nombre de participations à chaque challenge est soumis à un quota d'activations maximum. Autrement dit, il y a un nombre de joueurs maximum par challenge, défini par la Société Carrefour. Lorsqu'un challenge atteint ce quota, il apparaît comme étant « victime de son succès » au sein de l'application et devient indisponible à l’activation pour l’Adhérent. L’activation d’un challenge initialement affiché comme « victime de son succès » peut redevenir possible au cours du mois dans l'hypothèse où des joueurs se désinscrivent du challenge “victime de son succès”.</p>",
        '<p style="font-size: 12px;font-family: ProximaNova;">Dans le cas où un même challenge est disponible le mois suivant, les compteurs d’inscription, comme l’avancée du challenge, sont remis à zéro au début du nouveau challenge mensuel.</p>',
        '<h3 style="font-size: 14px;font-family: ProximaNova-Bold;">6- Communication clients </h3>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Les Adhérents participant à l’opération « Challenge Fid’ Carrefour » seront contactés deux fois par mois par un courrier électronique personnalisé, avec une url sécurisée le dirigeant vers le Site. Ce courrier électronique permettra à l’Adhérent de connaître l’avancement de chacun de son ou ses challenges. L’Adhérent pourra également suivre sa progression à tout moment depuis l’application Carrefour ou le Site.</p>',

        '<h3 style="font-size: 14px;font-family: ProximaNova-Bold;">7- Crédit de la Cagnotte Club Carrefour </h3>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Les Adhérents qui auront atteint leur(s) challenge(s) à la fin de la période, soit le dernier jour du mois, verront leur Cagnotte Club Carrefour créditée du montant indiqué sur l’application Carrefour ou sur le site pour chaque challenge, au plus tard le 5 du mois suivant.</p>',
        '<h3 style="font-size: 14px;font-family: ProximaNova-Bold;">8- Changement d’adresse électronique / Informations personnelles </h3>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Tout changement d’adresse, de nom, ou toute indication pouvant être nécessaire, devra être notifié à la Société Carrefour. L’Adhérent garantit l’exactitude des informations fournies et sera seul responsable de toutes les indications erronées, incomplètes ou obsolètes.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Il est également possible de procéder à la mise à jour des informations type adresse, numéro de téléphone, courriel, (…) via l’espace Mes Infos personnelles disponible depuis l’application Carrefour ou sur le site <a target="_blank" href="https://www.carrefour.fr" title="Accéder au site Carrefour">www.carrefour.fr</a>.</p>',
        '<h3 style="font-size: 14px;font-family: ProximaNova-Bold;">9- Utilisation des données à caractère personnel </h3>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="font-family: ProximaNova-Bold;">9.1.</span> En utilisant le Site et en s’inscrivant à « Challenge Fid’ Carrefour » du Programme Club Carrefour, l’Adhérent accepte l’utilisation de ses données personnelles par la Société Carrefour pour les besoins liés au fonctionnement du Site, de l’Application ainsi que du Programme Club Carrefour, suivant les conditions énoncées à l’article 10 des conditions générales du Programme « Le Club Carrefour » disponibles sur le site <a target="_blank" href="https://www.carrefour.fr/services/carte-carrefour/conditions-generales" title="Accéder aux conditions générales de Carrefour">http://www.carrefour.fr/services/carte-carrefour/conditions-generales</a> ci-après rappelé :</p>',
        '<p style="font-size: 13px;font-family: ProximaNova-Bold;">9.2. Cookies</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;font-style: italic;">L\'utilisation des cookies lors de votre visite sur notre Site</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Pour permettre aux utilisateurs du Site de bénéficier des services proposés par le Site tels que la consultation de produits, l’inscription à certains services (newsletters, mes magasins préférés, etc.), de l’optimisation de son utilisation et de sa personnalisation (notamment personnalisation des offres et des publicités) en fonction de l’utilisateur, le Site utilise des Cookies.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Sauf si vous décidez de désactiver les cookies, vous acceptez que le Site puisse les utiliser. Vous pouvez à tout moment désactiver ces cookies et ce gratuitement à partir des possibilités de désactivation qui vous sont offertes et rappelées ci-après, sachant que cela peut réduire ou empêcher l’accessibilité à tout ou partie des Services proposés par le Site.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;font-style: italic;">Qu’est-ce qu’un cookie et son utilité ?</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Lors de la consultation du Site, des informations relatives à la navigation de votre terminal (ordinateur, tablette, smartphone, etc.) sur le Site peuvent être enregistrées dans des fichiers texte appelés "Cookies", installés sur votre navigateur. Les cookies vont être utilisés pour reconnaître votre navigateur pendant la durée de validité du cookie concerné.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Seul l’émetteur du cookie concerné est susceptible de lire ou de modifier les informations qui y sont contenues.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Certains cookies sont indispensables à l´utilisation du Site, d´autres permettent d´optimiser l’utilisation du Site et de personnaliser les contenus affichés, ainsi les cookies permettent :</p>',
        '<ul style="margin-left: 25px;">',
        '<li style="font-size: 12px;font-family: ProximaNova;">De mesurer et d’analyser la fréquentation et d’utilisation du Site, de ses rubriques et Services proposés, nous permettant de réaliser des études et d’améliorer l’intérêt et l’ergonomie du Site et de nos Services,</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">De mémoriser les préférences d’affichage de votre navigateur (langue utilisée, paramètres d’affichage, système d’exploitation utilisé, etc ) et d’adapter la présentation du Site lors de vos visites, selon les matériels et logiciels de visualisation ou de lecture que comporte votre terminal et qui sont utilisés pour la navigation sur notre Site la navigation.</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">De vous permettre d’accéder à des espaces réservés et personnels du Site ou de nos Services, tels que votre compte personnel, grâce à des identifiants ou des données personnelles vous concernant antérieurement communiquées, vous permettant le cas échéant d’accéder à des contenus personnalisés,</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">De mettre en œuvre des mesures de sécurité,</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">De limiter le nombre de publicités affichées</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">D’adapter les contenus publicitaires insérés sur le Site à vos centres d’intérêts et, éventuellement, avec un rattachement à une localité (ex liste des magasins les plus proches),</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">D’adapter et vous adresser des publicités personnalisées qui vous sont spécifiquement destinées par voie de prospection électronique ou par affichage sur votre terminal au sein d’espaces publicitaires contenant des cookies que nous émettons. Ceci grâce aux données à caractère personnelles que vous nous communiquez notamment lors de votre inscription ou de l’accès à l’un de nos services et de son utilisation, auxquelles nous associons les Cookies que nous émettons.</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">Lorsque vous accédez à un site tiers contenant des espaces publicitaires diffusant une des nos annonces publicitaires, cette annonce peut vous être adressée via la reconnaissance d’un de nos Cookies que nous aurions précédemment déposé sur votre navigateur. Ces cookies peuvent être posés par nos prestataires techniques et utilisés.</li>',
        '</ul>',
        '<p style="font-size: 12px;font-family: ProximaNova;font-style: italic;">Les cookies déposés par un tiers sur des espaces de notre Site</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Notre site est susceptible de contenir des Cookies émis par des tiers (agence de communication, société de mesure d’audience, prestataire de publicité ciblée, etc.) permettant à ces derniers, pendant la durée de validité de leurs Cookies :</p>',
        '<ul style="margin-left: 25px;">',
        '<li style="font-size: 12px;font-family: ProximaNova;">de recueillir des informations de navigation relatives aux navigateurs consultant notre site,</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">de déterminer les contenus publicitaires susceptibles de correspondre à vos centres d’intérêts pour vous adresser des publicités ciblées, au regard de votre historique de navigation collecté par le tiers.</li>',
        '</ul>',
        '<p style="font-size: 12px;font-family: ProximaNova;">A tout moment vous pourrez empêcher la collecte d’informations vous concernant via ces cookies tiers, en cliquant sur les liens correspondant (voir le chapitre "Votre gestion et utilisation des cookies").</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">L’émission et l’utilisation de Cookies par ces entreprises sont soumises à leurs propres conditions d’utilisation.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;font-style: italic;">Les cookies intégrés à des applications tierces sur nos Sites ou nos Services</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Nous sommes susceptibles d’inclure sur notre site des applications informatiques émanant de tiers, qui vous permettent de partager des contenus de notre site avec d’autres personnes ou de faire connaître à ces autres personnes vos consultations ou vos opinions concernant un contenu de notre Site. Tel est notamment le cas des boutons "Partager", "J’aime", issus de réseaux sociaux tels que "Facebook", "X", "Viadéo", etc.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Le réseau social est susceptible de vous identifier grâce à ce bouton, même si vous ne l’avez pas utilisé lors de votre consultation de notre Site. Ce type de bouton applicatif peut permettre au réseau social concerné de suivre votre navigation sur notre Site, du seul fait que votre compte au réseau social était activé sur votre navigateur (session ouverte) durant votre navigation sur notre Site.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Nous ne disposons d’aucun contrôle sur le processus employé par les réseaux sociaux pour collecter des informations relatives à votre navigation sur notre Site et associées aux données personnelles dont ils disposent. Nous vous recommandons de consulter les conditions d’utilisation de vos données sur ces réseaux sociaux pour connaissance les finalités d’utilisation, notamment publicitaires, les informations de navigation qu’ils peuvent recueillir grâce à ces boutons applicatifs. Il convient de vous assurer que les conditions d’utilisation de ces réseaux sociaux peuvent vous permettre d’encadrer et de restreindre l’utilisation de vos données par ces réseaux sociaux, notamment en paramétrant vos comptes d’utilisation auprès de ces derniers.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;font-style: italic;">Partage de l’utilisation de votre terminal avec d’autres personnes</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Si votre terminal est utilisé par plusieurs personnes et lorsqu’un même terminal dispose de plusieurs logiciels de navigation, nous ne pouvons pas nous assurer de manière certaine que les services et publicités destinés à votre terminal correspondent bien à votre propre utilisation de ce terminal et non à celle d’un autre utilisateur de ce terminal.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Le partage avec d’autres personnes de l’utilisation de votre terminal et la configuration des paramètres de votre navigateur à l’égard des Cookies, relèvent de votre libre choix et de votre responsabilité.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;font-style: italic;">Votre gestion et utilisation des cookies</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Vous pouvez gérer et modifier à tout moment l’utilisation des cookies suivant les possibilités rappelées ci-après.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Les paramétrages que vous effectuerez sont susceptibles de modifier votre navigation sur internet et vos conditions d’accès et d’utilisation de certains services de notre Site qui nécessitent l’utilisation de cookies.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Ainsi vous pouvez gérer vos cookies :</p>',
        '<ul style="margin-left: 25px;">',
        '<li style="font-size: 12px;font-family: ProximaNova;">à partir de votre logiciel de navigation, ou</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">lorsque cela vous est proposé à partir d’un lien présent dans la liste ci-dessous.</li>',
        '</ul>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Attention : la prise en compte de votre désinscription repose sur un cookie. Par conséquent, si vous désactivez tous les cookies de votre terminal ou si vous changez de terminal, nous ne saurons plus que vous avez choisi cette option.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">1 . Gestion de vos cookies à partir de votre logiciel de navigation : Vous pouvez configurer votre logiciel de navigation de manière à ce que des Cookies soient enregistrés dans votre terminal ou qu’ils soient rejetés, soit systématiquement, soit selon leur émetteur.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Pour connaître les modalités applicables à la gestion des cookies stockés dans votre navigateur, nous vous invitons à consulter le menu d’aide de votre navigateur ainsi que la rubrique dédiée aux cookies et traceurs du site de la CNIL (Commission Nationale de l’Informatique & des Libertés) : <a target="_blank" href="https://www.cnil.fr/fr/cookies-et-autres-traceurs" title="Accéder à la page CNIL">https://www.cnil.fr/fr/cookies-et-autres-traceurs</a>.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">2 . Gestion en ligne de vos cookies à partir de plateformes interprofessionnelles : Vous pouvez également vous connecter sur le site Youronlinechoices, proposé par les professionnels de la publicité digitale regroupés au sein de l’association européenne EDAA (European Digital Advertising Alliance) et géré en France par l’Interactive Advertising Bureau France.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Vous pourrez ainsi connaître les entreprises inscrites à cette plate-forme et qui vous offrent la possibilité de refuser ou d’accepter les Cookies qu’elles utilisent pour adapter, selon qu’elles collectent, les publicités susceptibles d’être affichées sur votre navigateur : <a target="_blank" href="https://www.youronlinechoices.com/fr" title="Accéder au site Youronlinechoices">www.youronlinechoices.com/fr</a>.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Cette plate-forme européenne est partagée par des centaines de professionnels de la publicité sur Internet et constitue une interface centralisée vous permettant d’exprimer votre refus ou votre acceptation des Cookies tel que ci-dessus précisé.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Cette procédure n’empêchera pas l’affichage de publicités sur les sites Internet que vous visitez. Elle ne bloquera que les technologies qui permettent d’adapter des publicités à vos centres d’intérêts.</p>',
        '<p style="font-size: 13px;font-family: ProximaNova-Bold;">Woosmap Recommendation</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="text-decoration: underline;">Description :</span> Ce cookie est déposé sur l’ensemble de nos sites web, sites mobiles et applications mobiles par un tiers, la société Web Geo Services, afin de collecter des données relatives à votre localisation physique dans le but de vous permettre de bénéficier de certaines fonctionnalités de nos sites et/ou de nos services et/ou de ceux d’autres clients de Web Geo Services, telles que la communication d’adresses de magasins et/ou points de collecte les plus proches de vous, ainsi que leurs jours et horaires d’ouverture.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="text-decoration: underline;">Durée :</span> Conformément à la réglementation applicable, ce cookie a une durée de validité de 13 mois maximum. A l’issue de ce délai, votre consentement sera de nouveau sollicité par l’affichage du bandeau d’information.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="text-decoration: underline;">Traitement :</span> Les données de géolocalisation donnent lieu à un traitement, déclaré à la CNIL et dont le responsable est la société Web Geo Services, pour des finalités de géolocalisation sur les différents sites que vous fréquentez recourant à la technologie de Web Geo Services.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="text-decoration: underline;">Destinataire :</span> Notre société est la seule destinataire des données ainsi traitées, dans la limite des données collectées à partir de nos sites ou applications.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="text-decoration: underline;">Information :</span> Conformément à la loi « informatique et libertés » du 6 janvier 1978 modifiée, vous disposez d’un droit d’accès et de rectification aux informations qui vous concernent, que vous pouvez exercer en vous adressant à : Web Geo Services – Services consommateurs – 19, rue de la République 34000 Montpellier ou <a href="mailto:help@woosmap.com" title="Envoyer un email à Woosmap">help@woosmap.com</a>.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;"><span style="text-decoration: underline;">Opposition :</span> Vous pouvez également, pour des motifs légitimes, vous opposer au traitement des données vous concernant en vous connectant à : <a target="_blank" href="https://www.woosmap.com/" title="Accéder au site Woosmap">https://www.woosmap.com/</a>.</p>',
        '<p style="font-size: 13px;font-family: ProximaNova-Bold;">9.3. Lettre d’information</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Si l’Adhérent l’accepte lors de son inscription ou ultérieurement à tout moment en se connectant à cette même rubrique, il recevra des offres promotionnelles au travers de la lettre d’information de la Société Carrefour. L’Adhérent a la possibilité de se désabonner en cliquant sur le lien prévu à cet effet présent dans la lettre d’information.</p>',
        '<h3 style="font-size: 14px;font-family: ProximaNova-Bold;">10- Le Site </h3>',
        '<p style="font-size: 13px;font-family: ProximaNova-Bold;">10.1. Propriété intellectuelle</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Les textes et les éléments graphiques, leur assemblage dans le Site, et le Site lui-même sont la propriété exclusive de la Société Carrefour et sont protégés au titre de la législation applicable en matière de droit d’auteur. Les logos, signes distinctifs du Groupe Carrefour sont protégés au titre du droit des marques.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Toute détérioration des éléments présents sur le Site ou du Site, ainsi que, en l’absence d’autorisation exprès, toute reproduction, téléchargement, copie, modification, utilisation commerciale, totale ou partielle, des différents éléments du Site sont interdits et expose son ou ses auteurs à des poursuites.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">En accédant à ce Site, la Société Carrefour accorde à l’Adhérent une licence d’utilisation aux conditions suivantes :</p>',
        '<ul style="margin-left: 25px;">',
        '<li style="font-size: 12px;font-family: ProximaNova;">un droit d’usage privé, personnel et non transmissible sur le contenu du Site ou l’un des éléments qui le composent,</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">un droit de reproduction pour stockage aux fins de reproduction sur un écran monoposte, en un exemplaire, pour copie de sauvegarde ou tirage papier,</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">toute utilisation de documents issus du Site doit mentionner sa source.</li>',
        '</ul>',
        '<p style="font-size: 13px;font-family: ProximaNova-Bold;">10.2. Responsabilité</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;font-style: italic;">Fonctionnement et utilisation du Site</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">La Société Carrefour n’est tenue que d’une obligation de moyens en ce qui concerne le fonctionnement et la continuité du Site.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">La Société Carrefour ne pourra en aucun cas être tenu responsable :</p>',
        '<ul style="margin-left: 25px;">',
        '<li style="font-size: 12px;font-family: ProximaNova;">des vitesses d’accès au Site depuis d’autres sites Internet, des vitesses d’ouverture et de consultation des pages du Site, de ralentissements externes, de la suspension ou de l’inaccessibilité du service, de l’utilisation frauduleuse par des tiers de toutes les informations mises à disposition sur le Site</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">pour des faits dus à un cas de force majeure tel que défini ci-après, des pannes et des problèmes d’ordre technique concernant le matériel, des programmes, des logiciels de réseau Internet pouvant, le cas échéant, entraîner la suspension ou la cessation du service.</li>',
        '<li style="font-size: 12px;font-family: ProximaNova;">si le service proposé par le Site s’avère incompatible ou présente des dysfonctionnements avec certains logiciels, configurations, systèmes d’exploitation ou équipements de l’Adhérent.</li>',
        '</ul>',
        '<p style="font-size: 12px;font-family: ProximaNova;">L’Adhérent est responsable de toute perte de données, de fichiers ou de tous dommages qu’il pourrait subir du fait de l’absence de sauvegarde de ses données. La Société Carrefour conseille notamment à l’Adhérent de conserver une copie de l’intégralité des fichiers qu’il envoie à la Société Carrefour lors de sa demande</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Il incombe à l’Adhérent de protéger ses équipements techniques notamment contre toute forme de contamination par des virus et/ou de tentative d’intrusion.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">L’Adhérent est seul responsable de l’utilisation qu’il fait du service et ne saurait tenir responsable la Société pour toute réclamation et/ou procédure faite à son encontre. Il s’engage à faire son affaire personnelle de toute réclamation et/ou procédure formée contre la Société et qui se rattacherait à son utilisation du service.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">La Société Carrefour se réserve le droit d’apporter à ses services toutes les modifications et améliorations qu’elle jugera nécessaires ou utiles et ne sera pas responsable des dommages de toute nature pouvant survenir de ce fait.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Par ailleurs, la Société Carrefour se réserve le droit, sans préavis ni indemnité, d’arrêter temporairement ou définitivement un service ou l’ensemble des services proposés par elle sur le Site. La Société Carrefour ne sera pas responsable des dommages de toute nature pouvant survenir de ce fait.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;font-style: italic;">Lien externe</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Le Site peut contenir des liens hypertextes allant vers d’autres sites Internet, lesquels sont soumis à leurs propres règles d’utilisation et notamment concernant l’utilisation des données personnelles de l’Adhérent. Il est conseillé à l’Adhérent de prendre connaissance des règles d’utilisation de ces sites et notamment de celles applicables à leurs données personnelles. La Société Carrefour ne prend aucun engagement concernant ces autres sites Internet auquel l’Adhérent pourrait avoir accès via le Site notamment en ce qui concerne leur contenu, fonctionnement, l’accès à ces sites et quant à l’utilisation de leur données personnelles par ces derniers.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;font-style: italic;">Contenu illicite / Bonnes mœurs</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Toute action de la part de l’Adhérent qui aurait pour but de restreindre ou empêcher une autre personne d’utiliser le Site est strictement interdite. La Société rappelle à l’Adhérent qu’il ne pourra utiliser le Site et les informations qu’il contient que pour son usage privé.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">La Société Carrefour rappelle que l’Adhérent ne pourra utiliser le Site qu’à des fins légales et qu’il lui est interdit de publier ou de transmettre par le Site tout élément illicite, préjudiciable, diffamatoire, pornographique, haineux, raciste ou autrement attentatoire à la dignité humaine. Au cas où la Société serait informée de la présence de contenus illicites sur le Site, elle serait en droit d’en supprimer immédiatement le contenu. La Société Carrefour se réserve par ailleurs la possibilité de prendre toutes les mesures nécessaires pour faire respecter ses droits. La Société Carrefour est en droit d’effectuer une surveillance électronique des documents affichés et des zones publiques du Site pour vérifier leur conformité aux exigences ci-dessus. Il est également interdit de recueillir ou de stocker des informations personnelles sur d’autres clients. La Société Carrefour se réserve le droit de ne pas honorer les demandes contrevenant à l’ordre public ou aux bonnes mœurs.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;font-style: italic;">Preuve</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Sauf preuve contraire apportée par l’Adhérent, les systèmes d’enregistrement automatique de la Société Carrefour sont considérés comme valant preuve de la nature, du contenu et de la date de la demande de l’Adhérent.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;font-style: italic;">Force majeure</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Sont réputés événements de force majeure ceux qui imprévisibles, irrésistibles et extérieurs rendent impossible de façon absolue, le traitement de la demande de l’Adhérent dans les conditions prévues.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Tous les événements de force majeure, définis par la réglementation et la jurisprudence constituent, une cause de suspension ou d’extinction des obligations de la Société, si l’événement se prolonge au-delà d’un (1) mois. La Société ne saurait être tenue pour responsable d’un manquement en cas de survenance de l’un des événements.</p>',

        '<h3 style="font-size: 14px;font-family: ProximaNova-Bold;">11- Acceptation des conditions générales </h3>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Le fait de participer au « Challenge Fid’ Carrefour» entraîne l’acceptation entière et sans réserve des Conditions Générales du Programme de Fidélité et également des présentes conditions générales.</p>',
        '<h3 style="font-size: 14px;font-family: ProximaNova-Bold;">12- Réclamation éventuelle </h3>',
        '<p style="font-size: 12px;font-family: ProximaNova;">Les présentes conditions générales sont soumises au droit français.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">En cas de litiges l’Adhérent pourra effectuer ses réclamations auprès du Service Client de la Société afin de rechercher une solution amiable.</p>',
        '<p style="font-size: 12px;font-family: ProximaNova;">A défaut d’accord amiable entre les Parties, le litige sera soumis aux tribunaux français.</p>',
        '</div>',
      ],
      loyaltychallengeBis: {
        afterTitle: false,
        questions: [
          {
            title: 'Qu’est-ce que Boost Fid’ ?',
            content:
              "Les challenges Boost Fid' sont des challenges supplémentaires, ponctuels et mis en avant en magasin. Les mêmes marques sont proposées à tous les détenteurs de la Carte Club ou de la Carte PASS, avec des paliers personnalisés à vos habitudes d'achat. Vous pouvez retrouver sur cette page tous les challenges Boost Fid’ du moment.",
          },
          {
            title: 'Comment participer à Boost Fid’ ?',
            content:
              'Vous devez activer le ou les challenges proposés pour pouvoir en profiter. A partir de l’activation, vos achats sur les produits inclus dans le challenge Boost Fid’ et réalisés dans tous les points de vente physiques Carrefour (Hypermarchés, Market, Contact, City, Express), au Drive et en livraison à domicile (Carrefour Livré Chez Vous) comptent à condition de présenter votre Carte Club ou Carte PASS à chaque passage en caisse.',
          },
          {
            title: 'Combien de temps dure Boost Fid’ ?',
            content:
              'Chaque challenge Boost Fid’ a une date d’expiration qui lui est propre et que vous pouvez retrouver directement sur la page Internet Boost Fid’.',
          },
          {
            title: 'Comment se calcule ma cagnotte Boost Fid’ ?',
            content:
              'A chaque palier atteint, vous cumulez des euros sur votre cagnotte Boost Fid’.',
          },
          {
            title:
              'Quel montant est pris en compte pour les produits en promotion ?',
            content:
              'Le montant des achats pris en compte dans l’avancement du challenge Boost Fid’ sera le montant net après remises. Par exemple, si un produit X est vendu 2 € avec 0.20 € de remise immédiate en caisse, la société Carrefour prendra en compte le montant réellement payé par l’Adhérent pour le calcul, à savoir 1.80€.',
          },
          {
            title: 'Quand est-ce que ma cagnotte sera versée ?',
            content:
              "Le montant que vous avez cagnotté durant l'opération est versé sur votre Carte Club ou Carte PASS au plus tard six jours après l’opération.",
          },
        ],
      },
    },
    currentChallenges: {
      meta: {
        title: 'Challenges Fidélité | Carrefour',
        keywords:
          'Carrefour, Fidélité, Challenges, Avantages, Promotions, Catalogue',
        description:
          "Découvrez les Challenges Fidélité Carrefour et gagnez jusqu'à 50€ de remise sur vos prochains achats.",
      },
      howToPlay: {
        specificDisplayCondition: DISPLAY_CONDITION.DEFAULT,
      },
      background: '#f5f4f2',
      howDoesitWork: {
        color: 'white',
        fontFamily: 'var(--font-family-bold)',
        backgroundColor: '#0970E6',
        containerBackgroundColor: 'white',
        text: 'Comment ça marche ?',
        borderRadius: '4px',
        containerPadding: '2rem 0 0',
        marginBottom: '2rem',
        modal: {
          background: '#FFFFFF',
          title: {
            text: 'Comment ça marche ?',
            color: '#000000',
            fontFamily: 'var(--font-family-bold)',
          },
          closeButton: {
            imageUrl: 'assets/images/default/close-button.svg',
            alternativeText: 'Fermer le tutoriel',
            color: 'var(--challenge-primary-color)',
          },
          skipNowButton: {
            text: "Passer pour l'instant",
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
            backgroundColor: '#0970E6',
            borderRadius: '4px',
          },
        },
      },
      bisChallenges: {
        banner: {
          url: {
            mobile: 'assets/images/cetoine/boost-fid-banner-mobile.webp',
            desktop: 'assets/images/cetoine/boost-fid-banner-desktop.webp',
          },
          alternativeText: 'Cagnottez encore + avec des Challenges bonus !',
        },
        controls: {
          previous: {
            url: 'assets/images/cetoine/arrow-left-white.webp',
            alternativeText: 'Voir les challenges précédents',
          },
          next: {
            url: 'assets/images/cetoine/arrow-right-white.webp',
            alternativeText: 'Voir les challenges suivants',
          },
        },
      },
    },
    pastChallenges: {
      meta: {
        title: 'Historique Des Challenges | Carrefour',
        keywords:
          'Carrefour, Fidélité, Challenges, Avantages, Promotions, Catalogue, challenges fid, challenges boost fid',
        description:
          "Découvrez les Challenges Fidélité Carrefour et gagnez jusqu'à 50€ de remise sur vos prochains achats.",
      },
      banner: {
        visibility: true,
        title: 'Historique des Challenges',
        bannerAriaLabel: 'Page d’historique affichant les défis passés',
        background: {
          mobile:
            "#F59E25 url('assets/images/cetoine/mobile-cover.webp') center / cover no-repeat",
          tablet:
            "#F59E25 url('assets/images/cetoine/tablet-cover.webp') center / cover no-repeat",
          desktop:
            "#F59E25 url('assets/images/cetoine/desktop-cover.webp') center / cover no-repeat",
        },
        styles: {
          title: {
            margin: 'unset',
            fontSize: '25px',
            color: '#6F2F88',
            padding: '2rem',
            textAlign: 'center',
            textTransform: 'uppercase',
            fontFamily: 'ProximaNova-ExtraBold',
          },
          banner: {
            height: '230px',
            display: 'flex',
            alignItems: 'center',
            marginBottom: '2rem',
            justifyContent: 'center',
          },
        },
      },
      accordion: {
        variant: Variants.VARIANT_1,
        alternativeTextExpandMore: 'Réduire la section',
        alternativeTextExpandLess: 'Développer la section',
        icon: {
          imageUrl: 'assets/images/cetoine/club-logo.svg',
          alternativeText: 'logo du club',
        },
        date: {
          format: 'd MMMM yyyy',
          color: '#272F35',
          fontFamily: 'ProximaNova-Medium',
          template: '{firstDate} - {lastDate}',
        },
        reward: {
          color: '#0F3269',
          fontFamily: 'ProximaNova-Bold',
        },
      },
      noChallengesText: {
        color: '#272F35',
        fontFamily: 'var(--font-family)',
        text: 'Pas de challenge sélectionné',
      },
      noRewardsText: {
        color: '#272F35',
        fontFamily: 'var(--font-family)',
        text: 'Pas de récompense gagnée',
      },
    },
    sorry: {
      background: '#00529f',
      content: [
        '<div style="max-width: 400px; margin: 0 auto 2rem;"><img src="assets/images/cetoine/challenge-fid.webp" class="responsive-width" alt="carrefour logo"></div>',
        '<div style="font-size: 1.5rem; text-align: center; color: white; font-family: ProximaNova-Medium;">ChallengeFid n\'est pas disponible pour le moment</div>',
      ],
    },
    authPage: {
      variant: Variants.VARIANT_1,
      meta: {
        title: 'Challenges Fidélité | Carrefour | Authentification',
        keywords:
          'Carrefour, Fidélité, Challenges, Avantages, Promotions, Catalogue',
        description:
          "Challenges Fid avec les avantages Carrefour. Connectez-vous pour participer et gagner jusqu'à 50€ de remise sur vos prochains achats.",
      },
      header: {
        backgroundColor: 'white',
        desktopBackgroundImage: 'assets/images/cetoine/bg-login-desktop.webp',

        mobileTopBackgroundImage: 'assets/images/cetoine/login-top-mobile.webp',
        mobileBottomBackgroundImage:
          'assets/images/cetoine/login-bottom-mobile.webp',

        tabletTopBackgroundImage: 'assets/images/cetoine/login-top-tablet.webp',
        tabletBottomBackgroundImage:
          'assets/images/cetoine/login-bottom-tablet.webp',
        title: {
          label: "Challenge Fid'",
          color: '#6F2F88',
          fontFamily: 'var(--font-family-proximanova-extrabold)',
        },
        avec: {
          color: '#6F2F88',
          fontFamily: 'var(--font-family-proximanova-extrabold)',
          label: 'Avec Le',
        },
        clubLogo: {
          imageUrl: 'assets/images/cetoine/club-logo.svg',
          alternativeText: 'logo du club',
        },
        reward: {
          imageUrl: 'assets/images/cetoine/carrefour-badge.webp',
          alternativeText: 'Gagnez jusqu’à 50€ d’économies',
        },
        additionalText: {
          color: '#6F2F88',
          fontFamily: 'var(--font-family-proximanova-extrabold)',
          text: {
            mobile: 'par mois<br> sur vos<br> marques<br> préférées.',
            tablet: 'par mois <br>sur vos<br> marques<br> préférées.',
            desktop: 'par mois sur vos<br> marques préférées.',
          },
        },
        bisBannerConfig: {
          backgroundColor: '#6F2F88',
          logo: {
            imageUrl: 'assets/images/cetoine/boost-fid-logo.webp',
            alternativeText: 'Booster le Logo FID',
          },
          newBadge: {
            backgroundColor: '#F59E25',
            color: 'white',
            fontFamily: 'var(--font-family-medium)',
            label: 'Nouveau',
          },
          earnMoreLogoMobile: {
            imageUrl: 'assets/images/cetoine/earn-more-mobile.webp',
            alternativeText: 'Cagnottez encore + avec des challenges bonus !',
          },
          earnMoreLogoDesktop: {
            imageUrl: 'assets/images/cetoine/earn-more-desktop.webp',
            alternativeText: 'Cagnottez encore + avec des challenges bonus !',
          },
        },
        formWrapper: {
          backgroundColor: 'white',
          title: {
            color: '#0F3269',
            label: 'Connectez-vous et jouez',
            fontFamily: 'var(--font-family-bold)',
          },
          form: {
            fields: [
              {
                type: 'select',
                placeholder: '',
                defaultValue: 'carrefour',
                formControlName: 'card_type',
                validations: [
                  {
                    type: 'required',
                    message: 'This field required',
                  },
                ],
                options: [
                  {
                    value: 'carrefour',
                    label: 'Carte Club Carrefour',
                  },
                  {
                    value: 'pass_standard',
                    label: 'Carte PASS Standard',
                  },
                  {
                    value: 'pass_gold',
                    label: 'Carte PASS Premium',
                  },
                  {
                    value: 'pass_world_elite',
                    label: 'Carte PASS World Elite',
                  },
                ],
              },
              {
                group: true,
                type: 'number',
                formControlName: 'card_number',
                placeholder: 'N° Carte Club Carrefour (13 chiffres) *',
                validations: [
                  {
                    type: 'required',
                    message:
                      'Veuillez remplir ce champ avec le numéro de votre carte',
                  },
                  {
                    type: 'pattern',
                    pattern: '.{12}[0-9]',
                    message:
                      'Format de la carte non valide. Veuillez vérifier le numéro de votre carte',
                  },
                ],
              },
            ],
            additionalFields: [
              {
                type: 'checkbox',
                formControlName: 'lc_optin',
                placeholder: "J'accepte les modalités",
                validations: [
                  {
                    type: 'requiredTrue',
                    message: 'Champs requis',
                  },
                ],
              },
            ],
            submitButton: {
              color: '#FFFFFF',
              backgroundColor: '#0970E6',
              label: 'Je me connecte',
              fontFamily: 'var(--font-family-bold)',
            },
          },
          linkToCard: {
            ariaLabel: 'S’inscrire au programme de fidélité Carrefour',
            color: '#095ba5',
            fontFamily: 'var(--font-family)',
            icon: {
              imageUrl: 'assets/images/cetoine/card.svg',
              alternativeText: 'carrefour logo',
            },
            label: 'Obtenir ma Carte Club Carrefour pour participer',
            url: 'https://www.carrefour.fr/mon-compte/inscription-fidelite',
          },
          acceptModality: {
            ariaLabel: 'Accéder à la page d’aide pour voir les modalités',
            url: '../help',
            color: '#095ba5',
            label: 'VOIR LES MODALITÉS',
            fontFamily: 'var(--font-family-bold)',
            icon: {
              imageUrl: 'assets/images/tmp-cetoine/card.svg',
              alternativeText: 'carrefour logo',
            },
          },
          errorMessage:
            "Votre compte n'a pas été trouvé. Quelques jours peuvent être nécessaires pour pouvoir se connecter après la création d'une carte.",
        },
      },
      howToPlay: {
        specificDisplayCondition: DISPLAY_CONDITION.NEVER,
        title: {
          color: 'white',
          text: 'Comment ça marche ?',
          fontFamily: 'var(--font-family)',
        },
      },
      notes: [
        "<p style=\" margin: 2rem 0.5rem 0.5rem 0.9rem;var(--font-family);\"> Un crédit vous engage et doit être remboursé.Vérifiez vos capacités de remboursement avant de vous engager. La Carte PASS implique la souscription d'un crédit renouvelable. Elle permet lors de l'achat de payer au comptant et/ou à crédit (au TAEG révisable en vigueur au moment de son utilisation). Choix du paiement sur la touche correspondante en caisse. Délai légal de rétractation de 14 jours. Carte réservée aux particuliers majeurs sous réserve d'étude et d'acceptation par Carrefour Banque. Conditions tarifaires en vigueur au 01/01/2020 susceptibles de variations. </p>",
        '<p style="var(--font-family);margin: 0.5rem 0.9rem;"> CARREFOUR BANQUE, S.A. au capital de 101 346 956,72 euros, dont le siège social est situé au 1, Place Copernic - 91051 EVRY Cedex. RCS EVRY n° 313 811515. N° ORIAS : 07 027 516 (<a href="https://www.orias.fr" target="_blank">www.orias.fr</a>) </p>',
        '<p style="var(--font-family);margin: 0.5rem 0.9rem;"> Ci-après la liste des entités/activités du groupe Carrefour : Carma (Carrefour Assurance), Carrefour Banque, Carrefour Hypermarchés (Carfuel, Carrefour Isolation, Carrefour Location, Carrefour Pro, Carrefour Spectacles, Carte Carburant Pro, Prime Eco Travaux…), Carrefour Proximité (Sources), Carrefour Voyages, Croquetteland, Dejbox Services (Dejbox), Devotis (Carrefour Numérisation), Greenweez (Greenweez, Natiloo), Nasa (Carrefour Optique & Audition), Potager city, Quitoque, Smarteco (Supeco). </p>',
        '<p style="var(--font-family);margin: 0.5rem 0.9rem;"> Carrefour.fr utilise vos données pour le bon fonctionnement des services auxquels vous souscrivez et pour vous proposer des offres personnalisées en fonction de votre profil. Vos données sont aussi utilisées dans le cadre de la bonne gestion de notre relation clients, ainsi qu\'à des fins d\'études marketing et de communications. </p>',
        "<p style=\"var(--font-family);margin: 0.5rem 0.9rem;\"> Conformément à la réglementation sur la protection des données à caractère personnel, vous bénéficiez d'un droit à l'information, d'accès, de rectification et de suppression des données, d'un droit d'opposition pour motif légitime ou pour tout traitement lié à la prospection commerciale, d'un droit à la limitation du traitement, et d'un droit lié à la portabilité de vos données ainsi que du droit de définir des directives relatives au sort de vos données après votre décès, qui s'exercent en écrivant depuis votre espace \"Mon compte\" </p>",
        "<p style=\"var(--font-family);margin:0.5rem 0.9rem 1rem;\"> En tant que responsable du traitement, Carrefour France met en œuvre un traitement de données personnelles vous concernant aux fins de vous permettre de participer à notre Challenge Fid' et de recevoir, de Carrefour France, les bons plans, promotions et offres spéciales des sociétés du Groupe Carrefour. Vous pouvez à tout moment utiliser le lien de désabonnement intégré dans chaque mail de prospection. Vous disposez d'un droit d'accès, de rectification et d'effacement des informations vous concernant, de limitation du traitement, d’un droit à la portabilité de vos données, d'un droit au retrait de votre consentement et du droit de formuler des directives spécifiques et générales concernant la conservation, l'effacement et la communication de vos données post-mortem. Vous disposez également d’un droit d’opposition au traitement de vos données. Enfin, vous avez le droit d'introduire une réclamation auprès de la Commission nationale de l'informatique et des libertés. Pour en savoir plus sur la gestion de vos données et de vos droits par Carrefour France : cliquez <a target=\"_blank\" href=\"https://www.carrefour.fr/articles/politique-generale-protection-donnees-personnelles\" >ici</a >. </p>",
      ],
    },
    cookiePolicy: {
      template: [
        '<div class="container" style="max-width: 800px; margin: 30px auto; display: flex; flex-direction: column;">',
        '<div style="display: flex; justify-content: center; margin-bottom: 30px;">',
        '<button style="width: 150px; background-color: white; color: green; border-color: green; border-radius: 0; margin-right: 30px;" onclick="window.history.back();">Retour</button>',
        '<button id="ot-sdk-btn" class="ot-sdk-show-settings cookie-btn">Paramètres des cookies</button>',
        '</div>',
        '<div id="ot-sdk-cookie-policy"></div>',
        '</div>',
      ],
    },
  },
  components: {
    onboarding: {
      dialogLabel: 'Découverte des challenges',
      closeLabel: 'Fermer la découverte',
      // Cetoine header is VARIANT_4, so the welcome screen provides its own
      // campaign branding rather than reusing a header block.
      showHeaderPromise: false,
      // Full-bleed sunburst artwork behind the immersive screens (falls back to
      // the flat orange `--hdr-bg` while it loads / if it fails).
      background: {
        mobile: { imageUrl: 'assets/images/cetoine/onboarding-bg.webp' },
        tablet: { imageUrl: 'assets/images/cetoine/onboarding-bg.webp' },
        desktop: {
          imageUrl: 'assets/images/cetoine/onboarding-bg-desktop.webp',
        },
      },
      // `asideLabel` switches the promise to the wide typographic lockup:
      // "jusqu'à 50€ d'économies" beside "par mois sur vos marques préférées".
      // The asterisk points at the legal note under the CTA.
      // The whole welcome headline is one asset: "Challenge Fid' avec le Club
      // Carrefour" over the "jusqu'à 50€ d'économies" lockup.
      promise: {
        image: {
          imageUrl: 'assets/images/cetoine/onboarding-promise.webp',
          alternativeText:
            'Challenge Fid’ avec le Club Carrefour : jusqu’à 50€* d’économies par mois sur vos marques préférées',
        },
        backgroundColor: 'transparent',
        textColor: '#5E2A78',
      },
      welcome: {
        startLabel: 'Activer mes Challenges !',
        // Preview of the shopper's own first challenges, in place of a baked
        // hero image.
        cardStack: {
          pillLabel: 'Jusqu’à 10 coups de cœur. À vous de choisir.',
        },
        stepsBadge: {
          backgroundColor: '#5E2A78',
          textColor: '#F59E25',
        },
        steps: [
          {
            label: 'ACTIVEZ',
            sublabel: 'vos 10 challenges dès le 1er du mois',
          },
          {
            label: 'ACHETEZ',
            sublabel: 'les produits éligibles en magasin ou sur carrefour.fr',
          },
          { label: 'CAGNOTTEZ', sublabel: 'jusqu’à 50€' },
        ],
        legalNote: {
          text: '*Montant crédité sur la Cagnotte Club pour la finalisation de 10 challenges par mois. Si le cumul des gains de vos 10 challenges est inférieur à 50€, Carrefour complète la somme pour atteindre 50€ dès que l’ensemble des 10 progressions est finalisé. Voir les modalités sur la page',
          linkLabel: 'Challenge Fid’',
          linkUrl:
            'https://challengefid.carrefour.fr/fr/e4af9141-147d-454c-853d-621ec27173cb/help',
        },
      },
      swipe: {
        title: 'Choisissez vos favoris',
        subtitle: 'Sélectionnez jusqu’à 10 challenges.',
        hint: 'Glissez à gauche pour passer, à droite pour l’activer.',
        passLabel: 'Au suivant !',
        selectLabel: 'J’active',
        toast: {
          title: 'Super choix !',
          body: 'Le challenge est ajouté à votre sélection.',
        },
        counter: {
          selectedTemplate: '{n} / {max} sélectionnés',
          remainingTemplate: 'Encore {n} choix possibles',
          remainingSingularTemplate: 'Encore {n} choix possible',
        },
        allChallengesLabel: 'Voir tous les challenges',
      },
    },
    bisCampaignTabs: [
      {
        color: '#6B767C',
        label: 'Challenge Fid’',
        selectedColor: '#004CAB',
        selectedBorder: '4px solid #004cab',
        fontFamily: 'var(--font-family-medium)',
        campaignType: CampaignTypes.LOYALTYCHALLENGE,
      },
      {
        color: '#6B767C',
        label: 'Boost Fid’',
        selectedColor: '#004CAB',
        selectedBorder: '4px solid #004cab',
        fontFamily: 'var(--font-family-medium)',
        campaignType: CampaignTypes.LOYALTYCHALLENGE_BIS,
      },
    ],
    header: {
      variant: Variants.VARIANT_4,
      backgroundColor: '',
      bannerAriaLabel:
        'Bannière des défis actuels : Challenge Fid’ avec le club, jusqu’à 50€',
      backgroundImage: {
        mobile: {
          imageUrl: 'assets/images/cetoine/mobile-cover.webp',
        },
        tablet: {
          imageUrl: 'assets/images/cetoine/tablet-cover.webp',
        },
        desktop: {
          imageUrl: 'assets/images/cetoine/desktop-cover.webp',
        },
      },
      title: {
        color: '#6F2F88',
        fontFamily: 'var(--font-family-proximanova-extrabold)',
        label: "Challenge Fid'",
      },
      avec: {
        label: 'Avec Le',
        color: '#6F2F88',
        fontFamily: 'var(--font-family-proximanova-extrabold)',
      },
      clubLogo: {
        imageUrl: 'assets/images/cetoine/club-logo.svg',
        alternativeText: 'logo du club',
      },
      badge: {
        imageUrl: 'assets/images/cetoine/carrefour-badge.webp',
        alternativeText: 'Gagnez jusqu’à 50€ d’économies',
      },
      favBrands: {
        label: 'par mois<br>sur vos<br> marques<br> préférées.',
        color: '#6F2F88',
        fontFamily: 'var(--font-family-proximanova-extrabold)',
      },
      gotoButton: {
        color: 'white',
        label: 'Accéder à mes Challenges',
        fontFamily: 'var(--font-family-bold)',
        backgroundColor: 'var(--challenge-primary-color)',
        iconUrl: 'assets/images/cetoine/header-button-icon.webp',
      },
    },
    brands: {
      variant: Variants.VARIANT_2,
      brandsBgColor: 'white',
      bgColorDesktop: 'white',
      border: '1px solid #EFEFEF',
    },
    footer: {
      variant: Variants.VARIANT_1,
      mobileVisiblity: true,
      styles: {
        display: 'flex',
        padding: '30px 0',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#04509b',
      },
      linkStyles: {
        color: '#fff',
        fontSize: '16px',
        textDecoration: 'none',
        fontFamily: 'var(--font-family)',
      },
      links: [
        {
          url: 'help',
          label: 'Foire aux questions & mentions légales',
        },
      ],
    },
    tutorial: {
      background: '#004cab',
      carousel: {
        config: {
          sectionAriaLabel: 'Carrousel du tutoriel',
          controlsAriaLabel: 'Contrôles du carrousel',
          paginationAriaLabel: 'Indicateurs de diapositives',
          dots: {
            activeBg: 'white',
            inactiveBg: '#0F3269',
            ariaLabel: 'Aller à la diapositive {variable} de {variable}',
          },
          slideIndicatorLabel: 'Diapositive {variable} sur {variable}',
          controls: {
            leftIcon: {
              imageUrl: 'assets/images/cetoine/chevron-left.svg',
              alternativeText: 'Aller à la diapositive précédente',
            },
            rightIcon: {
              imageUrl: 'assets/images/cetoine/chevron-right.svg',
              alternativeText: 'Aller à la diapositive suivante',
            },
          },
        },
        items: {
          variant: Variants.VARIANT_2,
          styles: {
            description: {
              color: '#ffffff',
              fontFamily: 'var(--font-family)',
            },
            card: {
              boxShadow: 'none',
              borderRadius: '0',
              background: '#00529f',
              border: '2.5px solid #f4f5f6',
            },
          },
          steps: [
            {
              picture: {
                imageUrl: 'assets/images/cetoine/step1.webp',
                alternativeText:
                  "Dès le 1er du mois, activez jusqu'à {maxSelectedChallenge} challenges parmi les 20 challenges sélectionnés pour vous.",
              },
              description:
                'Dès le 1er du mois, activez jusqu’à {maxSelectedChallenge} challenges parmi les 20 challenges sélectionnés pour vous.',
            },
            {
              picture: {
                imageUrl: 'assets/images/cetoine/step2.webp',
                alternativeText:
                  'Achetez les produits éligibles en magasin ou sur Carrefour.fr et cumulez jusqu’à 50 €',
              },
              description:
                'Achetez les produits éligibles en magasin ou sur <a href="https://www.carrefour.fr/" target="_blank" rel="noopener noreferrer" style="font-family: var(--font-family-medium); color: white" title="Accéder au site Carrefour">carrefour.fr</a> et cumulez jusqu’à 50 €.',
            },
          ],
        },
      },
    },
    wallet: {
      variant: Variants.VARIANT_3,
      navbar: {
        backgroundColor: 'white',
        label: {
          color: '#0F3269',
          text: 'Votre cagnotte',
          fontFamily: 'var(--font-family-medium)',
        },
        amount: {
          color: '#0F3269',
          fontFamily: 'var(--font-family-medium)',
        },
        tooltip: {
          iconUrl: 'assets/images/cetoine/default-info-icon.svg',
          content: 'au plus tard le 6 du mois prochain',
          alternativeText: 'au plus tard le 6 du mois prochain',
        },
      },
      header: {
        backgroundColor: 'white',
        default: {
          label: {
            color: '#004CAB',
            text: 'Votre cagnotte <br> Challenge Fid’',
            fontFamily: 'var(--font-family-medium)',
          },
          amount: {
            color: '#0F3269',
            fontFamily: 'var(--font-family-bold)',
          },
        },
        bis: {
          label: {
            color: 'white',
            text: 'Vos gains Boost Fid’ <br> ce mois-ci !',
            fontFamily: 'var(--font-family-medium)',
          },
          amount: {
            color: 'white',
            tpl: '+{variable}',
            fontFamily: 'var(--font-family-bold)',
          },
          backgroundColor: '#6F2F88',
          icon: {
            alternativeText: 'Votre cagnotte Boost',
            url: 'assets/images/cetoine/wallet-boostfid-icon.webp',
          },
        },
      },
    },
    navbar: {
      variant: Variants.VARIANT_3,
      menuVariant: Variants.VARIANT_2,
      hasLayoutPaddingTop: true,
      dropdownLabel: 'Menu',
      fontFamily: 'var(--font-family)',
      openButton: {
        imageUrl: 'assets/images/default/menu.svg',
        buttonTitle: 'Accédez aux options du site',
        imageAlt: 'Accédez aux options du site',
      },
      logo: {
        url: 'current',
        imageUrl: 'assets/images/cetoine/retailer-logo.svg',
        alternativeText: 'carrefour logo',
        height: '40px',
        showOnMobile: true,
      },
      navbarAriaLabel: 'Navigation principale',
      items: [
        {
          url: 'current',
          label: 'Mes challenges',
          ariaLabel: 'Accéder à la page des Challenges Fid’',
        },
        {
          url: 'history',
          label: 'Historique',
          ariaLabel: 'Accéder à la page Historique des Challenges Fid’',
        },
        {
          url: 'help',
          label: 'Aide ?',
          ariaLabel: 'Accéder à la page d’aide des Challenges Fid’',
        },
      ],
    },
    countdown: {
      variant: Variants.VARIANT_1,
      showOnPages: [Pages.CURRENT],
      showAfterCampaignSummaryWhen: [],
      showOnNavbarWhen: [DeviceType.DESKTOP],
      showBeforeCampaignSummaryWhen: [DeviceType.MOBILE, DeviceType.TABLET],
      titleAppearing: ['row', 'row', 'row'],
      titleVerticalAlignment: ['center', 'center', 'center'],
      labelStyle: {
        color: '#333333',
        fontFamily: 'var(--font-family-medium)',
      },
      title: {
        color: '#333333',
        showTitleOnMobile: true,
        label: 'Fin de Challenge Fid’',
        fontFamily: 'var(--font-family-medium)',
      },
      numberStyle: {
        color: '#ffffff',
        backgroundColor: '#00529f',
        fontFamily: 'var(--font-family-medium)',
      },
      texts: {
        days: 'jours',
        hours: 'heures',
        minutes: 'mins',
        seconds: 'secs',
      },
    },
    challenges: {
      elements: {
        winCard: {
          variant: Variants.VARIANT_2,
          backgroundColor: '#F59E25',
          amount: {
            fontFamily: 'var(--font-family-bold)',
            color: 'white',
            template: '+ {variable}',
            imageUrl: 'assets/images/cetoine/win-rec.svg',
            alternativeText:
              'Félicitations ! Vous avez gagné {variable}€ en terminant ce défi !',
          },
          complementaryMsg: {
            text: 'dans votre cagnotte',
            fontFamily: 'var(--font-family-proximanova-extrabold)',
            color: '#6F2F88',
            backgroundImage: {
              imageUrl: 'assets/images/cetoine/club-logo.svg',
              alternativeText: 'logo du club',
            },
          },
        },
        selectionButton: {
          variant: Variants.VARIANT_1,
          catchPhrase: {
            amountBackgroundColor: '#6F2F88',
            imageUrl: 'assets/images/default/selection-rec.svg',
            alternativeText: "Gagnez jusqu'à {variable}€",
            amountLabel: "Gagnez jusqu'à",
            lineHeight: '29px',
          },
          buttonCatchPhrase: {
            buttonAmountFontFamily: 'var(--font-family-bold)',
            buttonAmountLabel: 'Activer ce challenge !',
            buttonAmountBackgroundColor: '#0970E6',
            borderRadius: '4px',
          },
        },
        unselectionButton: {
          variant: Variants.VARIANT_2,
          text: 'Désactiver',
          title: 'Désactiver ce défi',
          color: '#DA2029',
          fontFamily: 'var(--font-family-bold)',
        },
        outOfStock: {
          variant: Variants.VARIANT_1,
          backgroundColor: 'rgb(111, 47, 160, 0.60)',
          borderRadius: '16px',
          daysThreshold: 7,
          banner: {
            border: '2px solid white',
            textColor: 'white',
            borderRadius: '2px',
            backgroundColor: '#6F2F88',
            text: 'Victime de son succès',
            countdownText:
              'Vos challenges préférés<br/>reviennent dans {variable} {jour|jours}',
            fontFamily: 'var(--font-family-bold)',
          },
        },
        expireDate: {
          variant: Variants.VARIANT_1,
          color: '#6F2F88',
          backgroundColor: '#F59E25',
          fontFamily: 'var(--font-family)',
          defaultTemplate:
            '<span>Expire le {date} </span> <span style="font-family: var(--font-family-medium);">-</span> <span style="font-family: var(--font-family-medium);">Plus que {daysLeft} jour restant</span>',
          plurielTemplate:
            '<span>Expire le {date} </span> <span style="font-family: var(--font-family-medium);">-</span> <span style="font-family: var(--font-family-medium);">Plus que {daysLeft} jours restants</span>',
        },
      },
      step: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_2,
          bannerAlternativeText: 'Bannière des défis',
          logoAlternativeText: 'Bannière du logo',
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            height: '143px',
            backgroundColor: '#004CAB',
          },
          subHeader: {
            color: '#004CAB',
            backgroundColor: 'white',
            completedBackgroundColor: '#F59E25',
            useDescription: false,
            fontFamily: 'var(--font-family-medium)',
            fontSize: '12px',
          },
          body: {
            activeWording: 'Je gagne',
            passedWording: "J'ai gagné",
            progressbar: {
              containerColor: '#EFEFEF',
              progressColor: '#004CAB',
              spendLabel: 'Mes achats',
              labelColor: '#00529f',
              spendLabelTop: '25px',
            },
            rewards: {
              color: '#004CAB',
              fontFamily: 'var(--font-family-medium)',
              defaultPath: 'assets/images/default/reward-rec.svg',
              bubbleHeight: '55px',
              alternativeText: {
                reached: "J'ai gagné {variable}€",
                unreached: 'je gagne {variable}€',
                default: "J'ai gagné {variable}€",
              },
              backgroundColors: {
                reached: { fill: '#6F2F88', stroke: '#6F2F88' },
                unreached: { fill: 'white', stroke: '#004CAB' },
                default: { fill: '#EAEAEA', stroke: '#EAEAEA' },
              },
            },
            targets: {
              height: '30px',
              color: '#004CAB',
              borderRadius: '50%',
              backgroundColor: 'white',
              fontFamily: 'var(--font-family-bold)',
              targetUnReachedTitle: '',
              onIcon: {
                imageUrl: 'assets/images/cetoine/on.svg',
                onIconAlternativeText: 'je gagne {variable}€',
              },
              offIcon: {
                imageUrl: 'assets/images/cetoine/off.svg',
                offIconAlternativeText: 'J’ai gagné {variable}€',
              },
              border: '1px solid var(--challenge-primary-color)',
            },
          },
          footer: {
            color: '#004CAB',
            label: 'Mes achats',
            fontFamily: 'var(--font-family-bold)',
            spentAmount: {
              color: 'white',
              borderRadius: '40px',
              backgroundColor: '#004CAB',
              fontFamily: 'var(--font-family-bold)',
              icon: {
                imageUrl: 'assets/images/cetoine/caddie.svg',
                alternativeText: 'Icône de panier',
              },
            },
            productCheckButton: {
              ariaLabel: 'Cliquez pour vérifier les produits éligibles',
              color: '#004CAB',
              backgroundColor: '#F5FAFE',
              label: 'Produits éligibles',
              fontFamily: 'var(--font-family)',
              icon: {
                imageUrl: 'assets/images/cetoine/list.svg',
                alternativeText: 'Icône d’éligibilité du produit',
              },
            },
          },
        },
      },
      frequency: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_6,
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            backgroundImage: {
              bannerAriaLabel: 'Bannière de fréquence : Livré et c’est gagné',
              url: 'assets/images/cetoine/frequency-drive.webp',
            },
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: '#F59E25',
          },
          body: {
            firstLabel: {
              text: 'Dès {variable} {commande|commandes} drive',
              color: '#0F3269',
              fontFamily: 'var(--font-family-bold)',
            },
            secondLabel: {
              text: 'Commande de {variable}€ minimum - 1 commande maximum par jour',
              color: '#0F3269',
              fontFamily: 'var(--font-family)',
            },
            summary: {
              text: 'Je gagne',
              color: '#004cab',
            },
            totalBg: '#4CCD8A',
          },
          footer: {
            color: 'black',
            fontFamily: 'var(--font-family)',
            label: 'Plus que {variable} commandes pour gagner {variable}€',
            footerIcon: {
              imageUrl: 'assets/images/cetoine/frequency-info.svg',
              alternativeText: '',
            },
          },
        },
        [CetoineSubType.DRIVE]: {
          variant: Variants.VARIANT_6,
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            backgroundImage: {
              bannerAriaLabel: 'Bannière de fréquence : Livré et c’est gagné',
              url: 'assets/images/cetoine/frequency-drive.webp',
              title: {
                color: 'white',
                fontFamily: 'var(--font-family)',
                text: 'Votre 1ère commande Drive <br/> ou Livraison à Domicile<br/> récompensée !',
              },
            },
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: '#F59E25',
          },
          body: {
            firstLabel: {
              text: 'Dès {variable} {commande|commandes} drive',
              color: '#0F3269',
              fontFamily: 'var(--font-family-bold)',
            },
            secondLabel: {
              text: 'Commande de {variable}€ minimum - 1 commande maximum par jour',
              color: '#0F3269',
              fontFamily: 'var(--font-family)',
            },
            summary: {
              text: 'Je gagne',
              color: '#004cab',
            },
            totalBg: '#4CCD8A',
          },
          footer: {
            color: 'black',
            fontFamily: 'var(--font-family)',
            label: 'Plus que {variable} commandes pour gagner {variable}€',
            footerIcon: {
              alternativeText: '',
              imageUrl: 'assets/images/cetoine/frequency-info.svg',
            },
          },
        },
        [CetoineSubType.DRIVE_LOYALTY]: {
          variant: Variants.VARIANT_6,
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            backgroundImage: {
              bannerAriaLabel: 'Bannière de fréquence : Livré et c’est gagné',
              url: 'assets/images/cetoine/frequency-drive.webp',
            },
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: '#F59E25',
          },
          body: {
            firstLabel: {
              text: 'Dès {variable} {commande|commandes} drive',
              color: '#0F3269',
              fontFamily: 'var(--font-family-bold)',
            },
            secondLabel: {
              text: 'Commande de {variable}€ minimum - 1 commande maximum par jour',
              color: '#0F3269',
              fontFamily: 'var(--font-family)',
            },
            summary: {
              text: 'Je gagne',
              color: '#004cab',
            },
            totalBg: '#4CCD8A',
          },
          footer: {
            color: 'black',
            fontFamily: 'var(--font-family)',
            label: 'Plus que {variable} commandes pour gagner {variable}€',
            footerIcon: {
              alternativeText: '',
              imageUrl: 'assets/images/cetoine/frequency-info.svg',
            },
          },
        },
        [CetoineSubType.DRIVECLCV]: {
          variant: Variants.VARIANT_7,
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            backgroundImage: {
              bannerAriaLabel: 'Votre première commande récompensée',
              url: 'assets/images/cetoine/drive-clcv-recruitment.webp',
            },
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: '#F59E25',
            title: {
              color: '#004CAB',
              fontFamily: 'var(--font-family-medium)',
              label: "Minimum d'achat de {variable}€ par commande",
            },
          },
          body: {
            picture: {
              imageUrl: 'assets/images/cetoine/bag.svg',
              alternativeText: '',
            },
            summary: {
              text: 'Je gagne',
              color: '#004cab',
              fontFamily: 'var(--font-family-bold)',
            },
            visitWording: {
              text: 'commande',
              color: 'white',
              fontFamily: 'var(--font-family-medium)',
            },
          },
          footer: {
            color: 'black',
            fontFamily: 'var(--font-family)',
            footerIcon: {
              imageUrl: 'assets/images/cetoine/frequency-info.svg',
              alternativeText: '',
            },
            label: 'Plus que {variable} commandes pour gagner {variable}€',
          },
        },
        [CetoineSubType.DRIVE_CLCV_RECRUITMENT]: {
          variant: Variants.VARIANT_7,
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            backgroundImage: {
              bannerAriaLabel: 'Votre première commande récompensée',
              url: 'assets/images/cetoine/drive-clcv-recruitment.webp',
            },
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: '#F59E25',
            title: {
              color: '#004CAB',
              fontFamily: 'var(--font-family-medium)',
              label: "Minimum d'achat de {variable}€ par commande",
            },
          },
          body: {
            picture: {
              imageUrl: 'assets/images/cetoine/bag.svg',
              alternativeText: '',
            },
            summary: {
              text: 'Je gagne',
              color: '#004cab',
              fontFamily: 'var(--font-family-bold)',
            },
            visitWording: {
              text: 'commande',
              color: 'white',
              fontFamily: 'var(--font-family-medium)',
            },
          },
          footer: {
            color: 'black',
            fontFamily: 'var(--font-family)',
            footerIcon: {
              imageUrl: 'assets/images/cetoine/frequency-info.svg',
              alternativeText: '',
            },
            label: 'Plus que {variable} commandes pour gagner {variable}€',
          },
        },
        [CetoineSubType.CLCV]: {
          variant: Variants.VARIANT_8,
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            backgroundImage: {
              bannerAriaLabel: 'Bannière de fréquence : Livré et c’est gagné',
              url: 'assets/images/cetoine/frequency-clcv-loyalty.webp',
            },
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: '#F59E25',
          },
          body: {
            firstLabel: {
              text: 'Dès {variable} {livraison|livraisons} à domicile',
              color: '#0F3269',
              fontFamily: 'var(--font-family-bold)',
            },
            secondLabel: {
              text: 'Commande de {variable}€ minimum - 1 livraison maximum par jour',
              color: '#0F3269',
              fontFamily: 'var(--font-family)',
            },
            summary: {
              text: 'Je gagne',
              color: '#004cab',
            },
            totalBg: '#4CCD8A',
          },
          footer: {
            color: 'black',
            fontFamily: 'var(--font-family)',
            label: 'Plus que {variable} commandes pour gagner {variable}€',
            footerIcon: {
              imageUrl: 'assets/images/cetoine/frequency-info.svg',
              alternativeText: '',
            },
          },
        },
        [CetoineSubType.CLCV_LOYALTY]: {
          variant: Variants.VARIANT_8,
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            backgroundImage: {
              bannerAriaLabel: 'Bannière de fréquence : Livré et c’est gagné',
              url: 'assets/images/cetoine/frequency-clcv-loyalty.webp',
            },
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: '#F59E25',
          },
          body: {
            firstLabel: {
              text: 'Dès {variable} {livraison|livraisons} à domicile',
              color: '#0F3269',
              fontFamily: 'var(--font-family-bold)',
            },
            secondLabel: {
              text: 'Commande de {variable}€ minimum - 1 livraison maximum par jour',
              color: '#0F3269',
              fontFamily: 'var(--font-family)',
            },
            summary: {
              text: 'Je gagne',
              color: '#004cab',
            },
            totalBg: '#4CCD8A',
          },
          footer: {
            color: 'black',
            fontFamily: 'var(--font-family)',
            label: 'Plus que {variable} commandes pour gagner {variable}€',
            footerIcon: {
              imageUrl: 'assets/images/cetoine/frequency-info.svg',
              alternativeText: '',
            },
          },
        },
        [CetoineSubType.DRIVE_RECRUITMENT]: {
          variant: Variants.VARIANT_9,
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            backgroundImage: {
              bannerAriaLabel: 'Bannière de fréquence : Livré et c’est gagné',
              url: 'assets/images/cetoine/frequency-clcv_recruitment.webp',
              title: {
                color: 'white',
                fontFamily: 'var(--font-family-medium)',
                text: 'Votre 1ère commande<br/> Livraison à Domicile<br/> récompensée !',
              },
            },
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: '#F59E25',
            title: {
              color: '#004CAB',
              fontFamily: 'var(--font-family-medium)',
              label: "Minimum d'achat de 60€ par commande",
            },
          },
          body: {
            picture: {
              imageUrl: 'assets/images/cetoine/bag.svg',
              alternativeText: '',
            },
            summary: {
              text: 'Je gagne',
              color: '#004cab',
              fontFamily: 'var(--font-family-bold)',
            },
            visitWording: {
              text: 'commande',
              color: 'white',
              fontFamily: 'var(--font-family-medium)',
            },
          },
          footer: {
            color: 'black',
            fontFamily: 'var(--font-family)',
            footerIcon: {
              imageUrl: 'assets/images/cetoine/frequency-info.svg',
              alternativeText: '',
            },
            label: 'Plus que {variable} commandes pour gagner {variable}€',
          },
        },
        [CetoineSubType.CLCV_RECRUITMENT]: {
          variant: Variants.VARIANT_9,
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            backgroundImage: {
              bannerAriaLabel: 'Bannière de fréquence : Livré et c’est gagné',
              url: 'assets/images/cetoine/frequency-clcv_recruitment.webp',
              title: {
                color: 'white',
                fontFamily: 'var(--font-family-medium)',
                text: 'Votre 1ère commande<br/> Livraison à Domicile<br/> récompensée !',
              },
            },
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: '#F59E25',
            title: {
              color: '#004CAB',
              fontFamily: 'var(--font-family-medium)',
              label: "Minimum d'achat de 60€ par commande",
            },
          },
          body: {
            picture: {
              imageUrl: 'assets/images/cetoine/bag.svg',
              alternativeText: '',
            },
            summary: {
              text: 'Je gagne',
              color: '#004cab',
              fontFamily: 'var(--font-family-bold)',
            },
            visitWording: {
              text: 'commande',
              color: 'white',
              fontFamily: 'var(--font-family-medium)',
            },
          },
          footer: {
            color: 'black',
            fontFamily: 'var(--font-family)',
            footerIcon: {
              imageUrl: 'assets/images/cetoine/frequency-info.svg',
              alternativeText: '',
            },
            label: 'Plus que {variable} commandes pour gagner {variable}€',
          },
        },
        [CetoineSubType.LEX_RECRUITMENT]: {
          variant: Variants.VARIANT_10,
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            backgroundImage: {
              bannerAriaLabel:
                'Bannière de fréquence : Express lancé, défi gagné',
              url: 'assets/images/cetoine/frequency-lex.webp',
            },
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: '#F59E25',
            title: {
              color: '#004CAB',
              fontFamily: 'var(--font-family-medium)',
              label: "Minimum d'achat de {variable}€ par commande",
            },
          },
          body: {
            picture: {
              imageUrl: 'assets/images/cetoine/bag.svg',
              alternativeText: '',
            },
            summary: {
              text: 'Je gagne',
              color: '#004cab',
              fontFamily: 'var(--font-family-bold)',
            },
            visitWording: {
              text: 'livraison Express',
              color: 'white',
              fontFamily: 'var(--font-family-medium)',
            },
          },
          footer: {
            color: 'black',
            fontFamily: 'var(--font-family)',
            footerIcon: {
              imageUrl: 'assets/images/cetoine/frequency-info.svg',
              alternativeText: '',
            },
            label: 'Plus que {variable} commandes pour gagner {variable}€',
          },
        },
      },
    },
    challengeSummary: {
      variant: Variants.VARIANT_2,
      title: {
        color: '#333333',
        text: 'Mes challenges',
        fontFamily: 'var(--font-family-medium)',
      },
      update: {
        color: '#333333',
        text: 'Dernière mise à jour: {variable}',
        fontFamily: 'var(--font-family)',
        dateFormat: 'dd/MM/yyyy',
      },
      challengeLabels: {
        onGoingLabel: {
          color: '#0F3269',
          text: 'challenges sélectionnés',
          fontFamily: 'var(--font-family)',
        },
        completedLabel: {
          color: '#0F3269',
          text: '{challenge terminé|challenges terminés}',
          fontFamily: 'var(--font-family)',
        },
      },
      challengesInfos: {
        color: 'white',
        fontFamily: 'var(--font-family)',
      },
    },
    modals: {
      closeButton: {
        label: 'Fermer',
        textColor: 'white',
        borderRadius: '4px',
        backgroundColor: '#0970E6',
        fontFamily: 'var(--font-family-bold)',
        ariaLabel: 'Fermer cette boîte de dialogue',
      },
      unselectChallenge: {
        backgroundColor: '#FFFFFF',
        header: {
          logo: {
            alternativeText: 'Bannière de la marque',
          },
          backgroundColor: 'var(--challenge-primary-color)',
          backgroundImage: '',
        },
        frequencyHeaderImages: {
          [SubType.DEFAULT]: {
            imageUrl: 'assets/images/cetoine/frequency-drive.webp',
            alternativeText: 'défi de fréquence',
          },
          [SubType.DRIVE]: {
            imageUrl: 'assets/images/cetoine/frequency-drive.webp',
            alternativeText: 'défi de fréquence',
          },
          [SubType.DRIVE_LOYALTY]: {
            imageUrl: 'assets/images/cetoine/frequency-drive.webp',
            alternativeText: 'défi de fréquence',
          },
          [SubType.DRIVECLCV]: {
            imageUrl: 'assets/images/cetoine/drive-clcv-recruitment.webp',
            alternativeText: 'défi de fréquence',
          },
          [SubType.DRIVE_CLCV_RECRUITMENT]: {
            imageUrl: 'assets/images/cetoine/drive-clcv-recruitment.webp',
            alternativeText: 'défi de fréquence',
          },
          [SubType.CLCV]: {
            imageUrl: 'assets/images/cetoine/frequency-clcv-loyalty.webp',
            alternativeText: 'défi de fréquence',
          },
          [SubType.CLCV_LOYALTY]: {
            imageUrl: 'assets/images/cetoine/frequency-clcv-loyalty.webp',
            alternativeText: 'défi de fréquence',
          },
          [SubType.DRIVE_RECRUITMENT]: {
            imageUrl:
              'assets/images/cetoine/frequency-header-clcv_recruitment.webp',
            alternativeText: 'défi de fréquence',
          },
          [SubType.CLCV_RECRUITMENT]: {
            imageUrl:
              'assets/images/cetoine/frequency-header-clcv_recruitment.webp',
            alternativeText: 'défi de fréquence',
          },
          [SubType.LEX_RECRUITMENT]: {
            imageUrl: 'assets/images/cetoine/frequency-lex.webp',
            alternativeText: 'défi de fréquence',
          },
        },
        title: {
          color: 'black',
          fontFamily: 'var(--font-family-bold)',
          label:
            'Êtes-vous sûr(e) de vouloir <br/>déselectionner ce challenge ?',
        },
        body: {
          color: 'black',
          fontFamily: 'var(--font-family-light)',
          label:
            "Pas d'inquiétude, si vous changez d'avis, <br/>vous pourrez sélectionner ce challenge à nouveau.",
        },
        actionButtons: {
          variant: Variants.VARIANT_1,
          confirmButton: {
            label: 'Confirmer',
            borderRadius: '4px',
            textColor: '#FFFFFF',
            backgroundColor: '#0970E6',
            fontFamily: 'var(--font-family-bold)',
            ariaLabel: 'Fermer la boîte de dialogue en confirmant',
          },
          closeButton: {
            label: 'Annuler',
            borderRadius: '4px',
            textColor: '#0970E6',
            backgroundColor: 'white',
            borderColor: '#0970E6',
            fontFamily: 'var(--font-family-bold)',
            ariaLabel: 'Fermer la boîte de dialogue sans confirmer',
          },
        },
      },
      productCheck: {
        ariaLabel: '',
        fontFamily: '',
        backgroundColor: 'white',
        tabs: [
          {
            type: ProductCheckTabType.SCAN,
            checkMode: ScanCheckMode.PRODUCT_LIST,
            label: 'SCANNER',
            title: 'Scannez votre code barre !',
            readers: [BARCODE_FORMAT.EAN_8, BARCODE_FORMAT.EAN_13],
            successMessage: 'Succès',
            errorMessage: 'Non éligible pour ce challenge',
            startButtonLabel: 'Démarrer le scanner',
            retryPermissionButtonLabel: "Réessayer l'accès à la caméra",
            failureMessage: 'Une erreur est survenue, veuillez réessayer',
            restartButtonLabel: 'Redémarrer le scanner',
            errorWhenCameraPermissionDenied:
              "Veuillez autoriser l'accès à la caméra pour utiliser le scanner.",
            errorWhenCameraNotFound: 'Caméra non disponible',
            errorWhenCameraBusy:
              'La caméra est utilisée par une autre application. Fermez-la, puis réessayez.',
            previewLabel: 'Aperçu du scanner de code-barres',
          },
          {
            type: ProductCheckTabType.PRODUCT_LIST,
            failureMessage: 'Une erreur est survenue, veuillez réessayer',
            label: 'PRODUITS',
            searchable: true,
            inputPlaceholder: 'Saisissez un code-barres',
            tableTitle: {
              text: 'Liste des produits éligibles',
              color: 'var(--challenge-primary-color)',
            },
            columns: {
              name: {
                header: 'Produit',
                color: 'var(--challenge-primary-color)',
                fontFamily: 'var(--font-family-light)',
              },
              code: {
                header: 'Code barre',
                color: 'var(--challenge-primary-color)',
                fontFamily: 'var(--font-family-light)',
              },
            },
          },
        ],
        tabConfig: {
          activeColor: 'white',
          inactiveColor: 'black',
          inactiveBackground: 'white',
          fontFamily: 'var(--font-family)',
          inactiveBorderBottom: '2px solid #e6e6e6',
          activeBackground: 'var(--challenge-primary-color)',
          activeBorderBottom: '2px solid var(--challenge-primary-color)',
        },
      },
      firstSelection: {
        backgroundColor: 'white',
        title: {
          color: 'black',
          label: 'C’est un bon début !',
          fontFamily: 'var(--font-family-bold)',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/cetoine/first-selection.svg',
            alternativeText:
              'C’est un bon début ! Vous pouvez activer encore {variable} challenges ce mois-ci. Continuez de booster votre Cagnotte !',
          },
        },
        body: {
          color: 'black',
          fontFamily: 'var(--font-family-light)',
          label:
            'Vous pouvez activer encore {variable} challenges ce mois-ci. Continuez de booster votre Cagnotte !',
        },
      },
      lastSelection: {
        backgroundColor: 'white',
        title: {
          color: 'black',
          fontFamily: 'var(--font-family-bold)',
          label: ' Carton plein !',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/cetoine/last-selection.svg',
            alternativeText:
              'Vos {variable} challenges sont activés. Achetez les produits en ligne ou en magasin pour cagnotter.',
          },
        },
        body: {
          color: 'black',
          fontFamily: 'var(--font-family-light)',
          label:
            'Vos {variable} challenges sont activés. Achetez les produits correspondants en ligne ou en magasin pour cagnotter !',
        },
      },
      maximumSelection: {
        backgroundColor: 'white',
        title: {
          color: 'black',
          fontFamily: 'var(--font-family-bold)',
          label: 'La limite est atteinte',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/cetoine/maximum-selection.svg',
            alternativeText:
              "Vos {variable} challenges ont déjà été sélectionnés. Si vous le souhaitez, vous pouvez modifier l'un de vos choix en désélectionnant un challenge non commencé",
          },
        },
        body: {
          color: 'black',
          fontFamily: 'var(--font-family-light)',
          label:
            "Vos {variable} challenges ont déjà été sélectionnés. Si vous le souhaitez, vous pouvez modifier l'un de vos choix en désélectionnant un challenge non commencé.",
        },
      },
      eventDialog: {
        bottomSheet: {
          backgroundImage: {
            mobile: 'assets/images/cetoine/modal-bg-open-mobile.svg',
            tablet: 'assets/images/cetoine/modal-bg-open-desktop.svg',
            desktop: 'assets/images/cetoine/modal-bg-open-desktop.svg',
          },
          header: {
            label: {
              color: 'white',
              text: 'Nouveau',
              background: '#E61422',
              borderRadius: '5.6px',
              border: '1.4px solid #FFFFFF',
              fontFamily: 'var(--font-family-medium)',
            },
            title: {
              alternativeText: 'Challenges - E-Commerce',
              imageUrl: 'assets/images/cetoine/modal-title.svg',
            },
            infos: {
              alternativeText: "Icône d'information",
              icon: 'assets/images/cetoine/modal-info-icon.svg',
              content:
                "Les gains seront versés sur votre carte Carrefour au plus tard 15 jours après la fin de l'opération",
              visible: true,
              position: 'left',
            },
          },
          campaignInfo: {
            color: 'white',
            backgroundColor: '#E61422',
            fontFamily: 'var(--font-family)',
            template:
              'Du {variable} au {variable},&nbsp;<span style="font-family: var(--font-family-bold)"> cagnottez encore +</span>',
          },
          warningMsg: {
            color: '#242424',
            backgroundColor: '#FFD7D9',
            fontFamily: 'var(font-family-medium)',
            text: 'N’oubliez pas de sélectionner vos Challenge Fid’ !',
            icon: {
              alternativeText: 'Icône d’avertissement',
              url: 'assets/images/cetoine/warning-icon.svg',
            },
          },
          toggleButton: {
            alternativeText: 'Ouvrir la modale',
            alternativeTextExpanded: 'Fermer la modale',
            imageUrl: 'assets/images/cetoine/toggle-button.svg',
          },
        },
      },
      outOfStockIssue: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          label: 'Limite atteinte',
          fontFamily: 'var(--font-family-bold)',
        },
        body: {
          color: '#6b767c',
          fontFamily: 'var(--font-family)',
          label:
            'Ce challenge a atteint le nombre maximal de participants. Veuillez en choisir un autre.',
        },
      },
      genericError: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          label: 'Erreur',
          fontFamily: 'var(--font-family-bold)',
        },
        body: {
          color: '#6b767c',
          fontFamily: 'var(--font-family)',
          label:
            'Une erreur est survenue et le challenge n’a pas pu être sélectionné. Veuillez vérifier votre connexion et réessayer.',
        },
      },
      timeoutError: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          label: 'Délai dépassé',
          fontFamily: 'var(--font-family-bold)',
        },
        body: {
          color: '#6b767c',
          fontFamily: 'var(--font-family)',
          label:
            'La sélection du challenge a pris trop de temps. Veuillez vérifier votre connexion et réessayer.',
        },
      },
      challengeUnavailable: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          label: 'Campagne terminée',
          fontFamily: 'var(--font-family-bold)',
        },
        body: {
          color: '#6b767c',
          fontFamily: 'var(--font-family)',
          label:
            'Cette campagne est terminée, les challenges ne sont plus disponibles à la sélection.',
        },
      },
    },
  },
  tagging: {
    scriptLoader: {
      [ScriptPageKeys.AUTH]: {
        withId: false,
        pageName: 'login',
        pageTemplate: 'accueil',
      },
      [ScriptPageKeys.COOKIES_POLICY]: {
        withId: false,
        pageName: 'cookie-policy',
        pageTemplate: 'autres',
      },
      [ScriptPageKeys.CURRENT_CHALLENGES]: {
        withId: true,
        pageName: 'homepage',
        pageTemplate: 'accueil',
      },
      [ScriptPageKeys.FUTURE_CHALLENGES]: {
        withId: true,
        pageName: 'coming',
        pageTemplate: 'autres',
      },
      [ScriptPageKeys.HELP_PAGE]: {
        withId: true,
        pageName: 'faq',
        pageTemplate: 'autres',
      },
      [ScriptPageKeys.PAST_CHALLENGES]: {
        withId: true,
        pageName: 'history',
        pageTemplate: 'autres',
      },
    },
    tagEmitter: {
      [EmitterKeys.CHALLENGE_SELECT]: {
        level3: '',
        level2: 'deverouiller',
        eventName: 'challenge-fid-deverouiller',
      },
      [EmitterKeys.UNSELECT_CHALLENGE]: {
        level3: '',
        level2: 'supprimer-challenge',
        eventName: 'challenge-fid-supprimer',
      },
      [EmitterKeys.ELIGIBLE_PRODUCTS]: {
        level3: '',
        level2: 'produits-eligibles',
        eventName: 'challenge-fid-produits-eligibles',
      },
      [EmitterKeys.CONFIRM_UNSELECT_CHALLENGE]: {
        level3: '',
        level2: 'valider-supprimer-challenge',
        eventName: 'challenge-fid-valider',
      },
      [EmitterKeys.CANCEL_UNSELECT_CHALLENGE]: {
        level3: '',
        level2: 'annuler-supprimer-challenge',
        eventName: 'challenge-fid-annuler',
      },
      [EmitterKeys.LOGIN_BUTTON]: {
        level3: '',
        level2: 'participer',
        eventName: 'challenge-fid-participer',
      },
    },
  },
  scripts: [
    {
      loadInIframe: true,
      id: 'iframe-resizer',
      src: 'assets/js/cetoine/iframeResizer.contentWindow.min.js',
    },
  ],
};
